// src/components/SchoolAdmin/SchoolAdminDashboard.tsx

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import EnrollmentModal from "../EnrollmentModal/EnrollmentModal";
import auraWorldMapBg from "../../assets/aura-world-map-bg.png";
import ErhAlertPopup from "../AuraERH/ErhAlertPopup";

const API_BASE = (
  process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8000"
).replace(/\/+$/, "");

// ================= API =================
const LOGIN_ENDPOINT = `${API_BASE}/school/login`;
const PROFILE_ENDPOINT = `${API_BASE}/school/profile`;
const SCHOOL_ADMIN_DASHBOARD_ENDPOINT = `${API_BASE}/school/admin-dashboard`;
const USAGE_EXPORT_ENDPOINT = `${API_BASE}/school/usage/export`;
const PIN_REPORT_ENDPOINT = `${API_BASE}/school/pin-report.csv`;
const ENROLLMENT_STATUS_ENDPOINT = `${API_BASE}/school/enrollment-status`;
const SCHOOL_SUPPORT_EXPORT_ENDPOINT = `${API_BASE}/school/aura/report.csv`;
const SCHOOL_AURA_RESOURCE_PDF_ENDPOINT = `${API_BASE}/school/aura/resource.pdf`;
const SCHOOL_AURA_CASEY_CHAT_ENDPOINT = `${API_BASE}/school/aura/casey-chat`;
const SCHOOL_ROSTER_ENDPOINT = `${API_BASE}/school/roster`;
const SCHOOL_STUDENTS_ENDPOINT = `${API_BASE}/school/students`;
const SCHOOL_PT_ENDPOINT = `${API_BASE}/school/pt`;

// Version 1.2: enable the School Admin bulk enrollment workflow.
// The existing live roster manager remains available below it for single-record maintenance.
const SHOW_BETA_ENROLLMENT_SUBMISSIONS = true;

// ================= TYPES =================
type SchoolProfile = {
  org_id: number;
  org_code?: string;
  school_name?: string;
  org_name?: string;
  admin_name?: string;
  contact_name?: string;
  admin_email?: string;
  contact_email?: string;
};

type UsageByGrade = {
  grade: string | number;
  sessions?: number;
  minutes?: number;
  active_students?: number;
};

type AuraSummaryRow = {
  label: string;
  count: number;
  pct: number;
};

type LocalAura = {
  all?: {
    total_flagged?: number;
    by_severity?: AuraSummaryRow[];
    by_category?: AuraSummaryRow[];
    top_triggers?: AuraSummaryRow[];
  };
  forecast?: {
    risk_trend?: string;
    next_7d_flagged_interactions?: number;
    rising_categories?: string[];
  };
};

type DashboardPayload = {
  aura_enabled?: boolean;
  ui_state?: { message?: string };
  usage_by_grade?: UsageByGrade[];
  local_aura?: LocalAura | null;
};

type EnrollmentLatestSubmission = {
  id?: number;
  status?: string;
  created_at?: string;
  submitted_at?: string;
  approved_at?: string;
  rejected_at?: string;
  rows_count?: number;
  pin_report_ready?: boolean;
};

type EnrollmentStatusPayload = {
  ok?: boolean;
  org_id?: number;
  org_code?: string;
  latest_submission?: EnrollmentLatestSubmission | null;
  submission_id?: number | null;
  status?: string;
  pin_report_ready?: boolean;
  detail?: string;
};

type SchoolRosterStudent = {
  id: number;
  student_id?: string;
  name?: string;
  email?: string;
  grade?: string;
  pin?: string;
  pin_last4?: string;
  is_active?: boolean;
  active?: boolean;
};

type SchoolRosterPT = {
  id: number;
  pt_code?: string;
  role?: string;
  name?: string;
  email?: string;
  pin?: string;
  pin_last4?: string;
  is_active?: boolean;
  active?: boolean;
};

type SchoolRosterPayload = {
  ok?: boolean;
  students?: SchoolRosterStudent[];
  parent_teachers?: SchoolRosterPT[];
  parents_teachers?: SchoolRosterPT[];
  counts?: {
    students?: number;
    parent_teachers?: number;
  };
};

type LoginFormProps = {
  onLogin: (schoolId: string, pin: string) => Promise<void>;
  isSubmitting: boolean;
};

type AuraRange = "week" | "month" | "year";

type SeverityTone = "low" | "moderate" | "elevated" | "critical";

type ChartBucket = {
  label: string;
  fullLabel: string;
  incidentVolume: number;
  schoolRisk: number;
  highRisk: number;
};

type SchoolAdminCaseyMessage = {
  role: "casey" | "admin";
  text: string;
};

type SchoolAdminCaseyResponse = {
  ok?: boolean;
  reply?: string;
  metadata?: Record<string, unknown>;
};

// ================= HELPERS =================
function normalizeBearerToken(token: string): string {
  return String(token || "").replace(/^Bearer\s+/i, "").trim();
}

function readStoredToken(): string {
  return normalizeBearerToken(
    localStorage.getItem("school_admin_token") ||
      localStorage.getItem("access_token") ||
      ""
  );
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function toTitleCase(value: string): string {
  return String(value || "")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function formatDateRange(range: AuraRange): string {
  if (range === "week") return "Past 7 Days";
  if (range === "month") return "Past 30 Days";
  return "Past 12 Months";
}

function riskTone(value?: string): SeverityTone {
  const v = String(value || "").toLowerCase();
  if (v.includes("critical")) return "critical";
  if (v.includes("high") || v.includes("elevated")) return "elevated";
  if (v.includes("moderate") || v.includes("medium")) return "moderate";
  return "low";
}

function riskPercentFromAura(localAura?: LocalAura | null): number {
  const trend = String(localAura?.forecast?.risk_trend || "").toLowerCase();
  const total = Number(localAura?.all?.total_flagged || 0);
  const projected = Number(localAura?.forecast?.next_7d_flagged_interactions || 0);

  let base = 18;
  if (trend.includes("critical")) base = 88;
  else if (trend.includes("high") || trend.includes("elevated")) base = 68;
  else if (trend.includes("moderate")) base = 46;
  else if (trend.includes("low")) base = 24;

  const totalBoost = Math.min(20, total * 2);
  const projectedBoost = Math.min(15, projected);

  return clamp(Math.round(base + totalBoost + projectedBoost), 8, 98);
}

function formatStatusLabel(value?: string): string {
  if (!value) return "No Submission";
  return toTitleCase(value);
}

function formatDateTime(value?: string | null): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString();
}

function fmtShortDate(date: Date): string {
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function fmtMonth(date: Date): string {
  return date.toLocaleDateString(undefined, { month: "short" });
}

function startOfDay(date: Date): Date {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function addMonths(date: Date, months: number): Date {
  const next = new Date(date);
  next.setMonth(next.getMonth() + months);
  return next;
}

function buildYAxisTicks(max: number): number[] {
  const safeMax = Math.max(1, Math.ceil(max));

  const step =
    safeMax <= 10
      ? 2
      : safeMax <= 50
      ? 10
      : safeMax <= 100
      ? 20
      : safeMax <= 500
      ? 100
      : safeMax <= 1000
      ? 200
      : Math.ceil(safeMax / 5 / 100) * 100;

  const top = Math.ceil(safeMax / step) * step;

  return [top, top - step, top - step * 2, top - step * 3, 0].filter(
    (v, idx, arr) => v >= 0 && arr.indexOf(v) === idx
  );
}

function severityWeight(tone: SeverityTone): number {
  if (tone === "critical") return 4;
  if (tone === "elevated") return 3;
  if (tone === "moderate") return 2;
  return 1;
}

function buildLocalAuraBuckets(
  localAura: LocalAura | null | undefined,
  range: AuraRange,
  riskLevel: SeverityTone
): ChartBucket[] {
  const totalFlagged = Number(localAura?.all?.total_flagged || 0);
  const next7d = Number(localAura?.forecast?.next_7d_flagged_interactions || 0);
  const highRiskCount =
    Number(
      localAura?.all?.by_severity?.find((row) =>
        String(row.label || "").toLowerCase().includes("critical")
      )?.count || 0
    ) +
    Number(
      localAura?.all?.by_severity?.find((row) =>
        String(row.label || "").toLowerCase().includes("high")
      )?.count || 0
    ) +
    Number(
      localAura?.all?.by_severity?.find((row) =>
        String(row.label || "").toLowerCase().includes("elevated")
      )?.count || 0
    );

  const now = startOfDay(new Date());
  const toneWeight = severityWeight(riskLevel);

  if (range === "year") {
    const months = Array.from({ length: 12 }).map((_, idx) => {
      const monthDate = addMonths(now, idx - 11);
      return {
        date: monthDate,
        label: fmtMonth(monthDate),
        fullLabel: monthDate.toLocaleDateString(undefined, {
          month: "long",
          year: "numeric",
        }),
      };
    });

    return months.map((bucket, idx) => {
      const position = idx + 1;
      const wave = 0.75 + Math.abs(Math.sin(position * 0.82)) * 0.55;
      const incidents = Math.round((totalFlagged / 12) * wave);
      const highRisk = Math.round((highRiskCount / 12) * wave);
      const risk = incidents * toneWeight + highRisk * 2;

      return {
        label: bucket.label,
        fullLabel: bucket.fullLabel,
        incidentVolume: incidents,
        schoolRisk: risk,
        highRisk,
      };
    });
  }

  const dayCount = range === "week" ? 7 : 30;
  const dates = Array.from({ length: dayCount }).map((_, idx) =>
    addDays(now, idx - (dayCount - 1))
  );

  return dates.map((date, idx) => {
    const position = idx + 1;
    const base = totalFlagged / dayCount;
    const wave = 0.7 + Math.abs(Math.sin(position * 0.92)) * 0.75;
    const projectedBoost = range === "week" ? next7d / 7 : next7d / 30;

    const incidents = Math.round(base * wave + projectedBoost * (position / dayCount));
    const highRisk = Math.round((highRiskCount / dayCount) * wave);
    const risk = incidents * toneWeight + highRisk * 2;

    return {
      label: fmtShortDate(date),
      fullLabel: date.toLocaleDateString(),
      incidentVolume: incidents,
      schoolRisk: risk,
      highRisk,
    };
  });
}

async function fetchJson<T>(url: string, options?: RequestInit): Promise<T> {
  const response = await fetch(url, options);

  let data: any = null;
  try {
    data = await response.json();
  } catch {
    data = null;
  }

  if (!response.ok) {
    throw new Error(data?.detail || data?.message || response.statusText || "Request failed");
  }

  return data as T;
}

async function fetchJsonWithFallback<T>(urls: string[], options?: RequestInit): Promise<T> {
  let lastError: unknown = null;

  for (const url of urls) {
    try {
      return await fetchJson<T>(url, options);
    } catch (error: unknown) {
      lastError = error;
      const message = error instanceof Error ? error.message.toLowerCase() : "";
      if (!message.includes("404") && !message.includes("not found") && !message.includes("method not allowed")) {
        throw error;
      }
    }
  }

  throw lastError instanceof Error ? lastError : new Error("Request failed");
}

async function downloadFile(
  url: string,
  filename: string,
  headers?: Record<string, string>
) {
  const response = await fetch(url, {
    method: "GET",
    mode: "cors",
    headers: {
      Accept: "application/pdf,text/csv,text/html,*/*",
      ...(headers || {}),
    },
  });

  if (!response.ok) {
    let detail = "Download failed";
    try {
      const payload = await response.json();
      detail = payload?.detail || payload?.message || detail;
    } catch {
      try {
        detail = (await response.text()) || detail;
      } catch {
        // keep default
      }
    }
    throw new Error(detail);
  }

  const blob = await response.blob();
  const blobUrl = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = blobUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.URL.revokeObjectURL(blobUrl);
}


type ResourceAudience = "Students" | "Teachers" | "Parents" | "Administrators" | "School Community";
type ResourceActionKind = "preview" | "download" | "print" | "email" | "favorite";

type AuraResourceCategory = {
  id: string;
  title: string;
  icon: string;
  tagline: string;
  caseyRecommendation: string;
  keywords: string[];
};

type RecommendedResource = {
  id: string;
  title: string;
  type: string;
  audience: string;
  action: string;
  description: string;
  topic?: string;
  categoryId?: string;
  icon?: string;
  resourceKind?: "Poster" | "Classroom Lesson" | "Teacher Guide" | "Parent Guide" | "Administrator Playbook" | "Student Activity" | "Wellness Tip";
  staticPdfPath?: string;
  tags?: string[];
  estimatedTime?: string;
  featured?: boolean;
};

const AURA_RESOURCE_CATEGORIES: AuraResourceCategory[] = [
  {
    id: "digital-influence",
    title: "Digital Influence & Media Literacy",
    icon: "🌎",
    tagline: "Think Before You Click",
    caseyRecommendation:
      "Use these resources when AURA detects viral challenges, misinformation, online pressure, risky social media trends, deepfakes, or unsafe digital behavior.",
    keywords: ["digital", "viral", "internet", "challenge", "social", "media", "misinformation", "online", "tiktok", "deepfake"],
  },
  {
    id: "bullying-prevention",
    title: "Bullying Prevention",
    icon: "🛡️",
    tagline: "Be Kind. Be Strong. Be You.",
    caseyRecommendation:
      "Start here when AURA shows bullying, cyberbullying, exclusion, peer conflict, cruelty, harassment, or repeated social harm.",
    keywords: ["bully", "bullying", "cyber", "harassment", "upstander", "cruel", "teasing"],
  },
  {
    id: "suicide-prevention",
    title: "Suicide Prevention",
    icon: "💚",
    tagline: "You Matter. We Listen.",
    caseyRecommendation:
      "Use these tools for warning signs, trusted-adult bridges, crisis response planning, postvention, and parent or staff communication.",
    keywords: ["suicide", "self harm", "warning", "988", "hopeless", "crisis", "postvention"],
  },
  {
    id: "drug-medication-safety",
    title: "Drug & Medication Safety",
    icon: "💊",
    tagline: "One Choice Can Change Everything",
    caseyRecommendation:
      "Deploy these resources when students mention vaping, drugs, pills, cold medicine, Benadryl, fentanyl risk, or online medication challenges.",
    keywords: ["drug", "medication", "cold", "benadryl", "pill", "substance", "vaping", "fentanyl", "overdose"],
  },
  {
    id: "grief-loss",
    title: "Grief & Loss",
    icon: "🕯️",
    tagline: "Healing Together",
    caseyRecommendation:
      "Use these resources after a death, traumatic loss, teacher loss, student loss, suicide loss, or any schoolwide grief event.",
    keywords: ["grief", "loss", "death", "died", "mourning", "memorial", "healing"],
  },
];

const AURA_RESOURCE_LIBRARY: RecommendedResource[] = [
  // Digital Influence & Media Literacy
  { id: "digital-poster", categoryId: "digital-influence", topic: "Digital Influence & Media Literacy", icon: "🌎", title: "Think Before You Click", type: "Hallway Poster", resourceKind: "Poster", audience: "Students + Families", action: "Post immediately", estimatedTime: "Visual awareness", featured: true, staticPdfPath: "/uploads/aura_resources/aura_think_before_you_click_poster.pdf", description: "A bold hallway campaign reminding students to pause, question, and verify before trusting viral content, challenges, rumors, or AI-generated images.", tags: ["poster", "viral", "media literacy", "AI images"] },
  { id: "digital-lesson", categoryId: "digital-influence", topic: "Digital Influence & Media Literacy", icon: "🌎", title: "Pause. Question. Verify.", type: "Classroom Lesson", resourceKind: "Classroom Lesson", audience: "Students", action: "Use this week", estimatedTime: "20 minutes", description: "A short media-literacy lesson for viral challenges, risky trends, misinformation, online pressure, and fake digital content.", tags: ["lesson", "student", "misinformation"] },
  { id: "digital-teacher-guide", categoryId: "digital-influence", topic: "Digital Influence & Media Literacy", icon: "🌎", title: "When Curiosity Becomes Risk", type: "Teacher Discussion Guide", resourceKind: "Teacher Guide", audience: "Teachers / Counselors", action: "Use for advisory or small groups", estimatedTime: "15 minutes", description: "Teacher prompts for helping students separate curiosity from danger when online content makes unsafe behavior look exciting or harmless.", tags: ["teacher", "discussion", "risk"] },
  { id: "digital-parent-guide", categoryId: "digital-influence", topic: "Digital Influence & Media Literacy", icon: "🌎", title: "Talking About Social Media", type: "Parent Conversation Guide", resourceKind: "Parent Guide", audience: "Parents / Caregivers", action: "Send home", estimatedTime: "Family conversation", description: "A calm parent guide for discussing TikTok, social media challenges, peer pressure, misinformation, and online safety without shame or panic.", tags: ["parent", "social media", "conversation"] },
  { id: "digital-admin-playbook", categoryId: "digital-influence", topic: "Digital Influence & Media Literacy", icon: "🌎", title: "School Digital Safety Action Plan", type: "Administrator Playbook", resourceKind: "Administrator Playbook", audience: "Administrators", action: "Use for schoolwide response", estimatedTime: "Action plan", description: "A school leadership playbook for responding to viral trends, online rumors, harmful challenges, and digital safety incidents.", tags: ["administrator", "playbook", "schoolwide"] },
  { id: "digital-student-activity", categoryId: "digital-influence", topic: "Digital Influence & Media Literacy", icon: "🌎", title: "Spot the Fake Challenge", type: "Student Activity", resourceKind: "Student Activity", audience: "Students", action: "Use in class or advisory", estimatedTime: "10 minutes", description: "A quick student activity that teaches learners to identify missing context, manipulation, fake images, misleading captions, and unsafe online pressure.", tags: ["activity", "student", "fake"] },

  // Bullying Prevention
  { id: "bullying-poster", categoryId: "bullying-prevention", topic: "Bullying Prevention", icon: "🛡️", title: "Be Kind. Be Strong. Be You.", type: "Hallway Poster", resourceKind: "Poster", audience: "Students + Staff", action: "Post immediately", estimatedTime: "Visual awareness", featured: true, staticPdfPath: "/uploads/aura_resources/aura_bullying_prevention_poster.pdf", description: "A hallway poster that defines bullying, encourages upstander behavior, and reminds students that kindness is power.", tags: ["poster", "bullying", "upstander"] },
  { id: "bullying-lesson", categoryId: "bullying-prevention", topic: "Bullying Prevention", icon: "🛡️", title: "Building Respect", type: "Classroom Lesson", resourceKind: "Classroom Lesson", audience: "Students", action: "Use this week", estimatedTime: "20 minutes", description: "A classroom lesson focused on respect, inclusion, dignity, online behavior, and safe ways to get help.", tags: ["lesson", "respect", "student"] },
  { id: "bullying-teacher-guide", categoryId: "bullying-prevention", topic: "Bullying Prevention", icon: "🛡️", title: "Classroom Intervention Scripts", type: "Teacher Guide", resourceKind: "Teacher Guide", audience: "Teachers", action: "Use immediately", estimatedTime: "Quick script", description: "Firm teacher language for stopping bullying without putting the targeted student on display or turning the moment into a public investigation.", tags: ["teacher", "script", "intervention"] },
  { id: "bullying-parent-guide", categoryId: "bullying-prevention", topic: "Bullying Prevention", icon: "🛡️", title: "Helping a Child Being Bullied", type: "Parent Guide", resourceKind: "Parent Guide", audience: "Parents / Caregivers", action: "Send home", estimatedTime: "Family support", description: "A parent guide for listening, documenting, supporting the child emotionally, and partnering with school staff safely.", tags: ["parent", "bullying", "support"] },
  { id: "bullying-admin-playbook", categoryId: "bullying-prevention", topic: "Bullying Prevention", icon: "🛡️", title: "Schoolwide Bullying Response", type: "Administrator Playbook", resourceKind: "Administrator Playbook", audience: "Administrators", action: "Use for coordinated response", estimatedTime: "Action plan", description: "A schoolwide response framework for targeted students, students causing harm, witnesses, documentation, family communication, and follow-up.", tags: ["administrator", "response", "policy"] },
  { id: "bullying-student-activity", categoryId: "bullying-prevention", topic: "Bullying Prevention", icon: "🛡️", title: "Upstander Challenge", type: "Student Activity", resourceKind: "Student Activity", audience: "Students", action: "Use in advisory", estimatedTime: "10 minutes", description: "A student challenge that practices safe ways to speak up, reach out, refuse to join, and report harm to trusted adults.", tags: ["activity", "upstander", "student"] },

  // Suicide Prevention
  { id: "suicide-poster", categoryId: "suicide-prevention", topic: "Suicide Prevention", icon: "💚", title: "You Matter. We Listen.", type: "Hallway Poster", resourceKind: "Poster", audience: "Students + Staff", action: "Post in approved locations", estimatedTime: "Visual awareness", featured: true, staticPdfPath: "/uploads/aura_resources/aura_suicide_prevention_poster.pdf", description: "A supportive poster reminding students that they matter, warning signs should be taken seriously, and urgent help is available.", tags: ["poster", "suicide prevention", "988"] },
  { id: "suicide-lesson", categoryId: "suicide-prevention", topic: "Suicide Prevention", icon: "💚", title: "Hope, Connection & Help", type: "Classroom Lesson", resourceKind: "Classroom Lesson", audience: "Students", action: "Use with counselor guidance", estimatedTime: "20 minutes", description: "A prevention lesson centered on hope, trusted adults, connection, help-seeking, and the message that pain can be shared safely.", tags: ["lesson", "hope", "trusted adult"] },
  { id: "suicide-teacher-guide", categoryId: "suicide-prevention", topic: "Suicide Prevention", icon: "💚", title: "Responding to Warning Signs", type: "Teacher Guide", resourceKind: "Teacher Guide", audience: "Teachers / Counselors", action: "Use immediately", estimatedTime: "Safety response", description: "Teacher guidance for recognizing concerning statements or behavior, staying calm, not promising secrecy, and connecting the student to urgent school support.", tags: ["teacher", "warning signs", "crisis"] },
  { id: "suicide-parent-guide", categoryId: "suicide-prevention", topic: "Suicide Prevention", icon: "💚", title: "Starting Difficult Conversations", type: "Parent Guide", resourceKind: "Parent Guide", audience: "Parents / Caregivers", action: "Send after approval", estimatedTime: "Family conversation", description: "A parent guide for opening direct, compassionate conversations about emotional pain, safety, warning signs, and immediate support.", tags: ["parent", "conversation", "safety"] },
  { id: "suicide-admin-playbook", categoryId: "suicide-prevention", topic: "Suicide Prevention", icon: "💚", title: "Suicide Postvention Response", type: "Administrator Playbook", resourceKind: "Administrator Playbook", audience: "Administrators", action: "Use with crisis team", estimatedTime: "Crisis protocol", description: "A school postvention playbook for communication, safe language, avoiding method details, monitoring at-risk students, and coordinating supports.", tags: ["administrator", "postvention", "crisis team"] },
  { id: "suicide-student-activity", categoryId: "suicide-prevention", topic: "Suicide Prevention", icon: "💚", title: "Trusted Adult Bridge", type: "Student Activity", resourceKind: "Student Activity", audience: "Students", action: "Use anytime", estimatedTime: "5 minutes", description: "A private reflection tool that helps students identify one trusted adult, one safe next step, and one way to ask for help.", tags: ["activity", "trusted adult", "student"] },

  // Drug & Medication Safety
  { id: "drug-poster", categoryId: "drug-medication-safety", topic: "Drug & Medication Safety", icon: "💊", title: "One Choice Can Change Everything", type: "Hallway Poster", resourceKind: "Poster", audience: "Students + Families", action: "Post immediately", estimatedTime: "Visual awareness", featured: true, staticPdfPath: "/uploads/aura_resources/aura_drug_safety_poster.pdf", description: "A drug-safety poster focused on smart choices, medication risks, fake pills, overdose danger, and help-seeking.", tags: ["poster", "drug safety", "medication"] },
  { id: "drug-lesson", categoryId: "drug-medication-safety", topic: "Drug & Medication Safety", icon: "💊", title: "Medication Misuse & Social Media", type: "Classroom Lesson", resourceKind: "Classroom Lesson", audience: "Students", action: "Use within 48 hours", estimatedTime: "20 minutes", description: "A prevention lesson about cold medicine, cough medicine, Benadryl, pills from friends, online drug trends, and the myth that common products are harmless.", tags: ["lesson", "medication misuse", "social media"] },
  { id: "drug-teacher-guide", categoryId: "drug-medication-safety", topic: "Drug & Medication Safety", icon: "💊", title: "Recognizing Warning Signs", type: "Teacher Guide", resourceKind: "Teacher Guide", audience: "Teachers / Coaches", action: "Share with staff", estimatedTime: "Staff briefing", description: "A staff guide for noticing warning signs, responding privately, avoiding public shame, and involving the nurse, counselor, administrator, and family process.", tags: ["teacher", "warning signs", "nurse"] },
  { id: "drug-parent-guide", categoryId: "drug-medication-safety", topic: "Drug & Medication Safety", icon: "💊", title: "Safe Medication Conversations", type: "Parent Guide", resourceKind: "Parent Guide", audience: "Parents / Caregivers", action: "Send if trend is spreading", estimatedTime: "Family conversation", description: "A parent guide encouraging secure medication storage, calm conversations, symptom awareness, and immediate help when ingestion is suspected.", tags: ["parent", "medication", "conversation"] },
  { id: "drug-admin-playbook", categoryId: "drug-medication-safety", topic: "Drug & Medication Safety", icon: "💊", title: "School Drug Prevention Plan", type: "Administrator Playbook", resourceKind: "Administrator Playbook", audience: "Administrators", action: "Use for prevention planning", estimatedTime: "Action plan", description: "A leadership playbook for prevention messaging, staff awareness, parent communication, nurse/counselor workflow, and trend monitoring.", tags: ["administrator", "prevention", "school plan"] },
  { id: "drug-student-activity", categoryId: "drug-medication-safety", topic: "Drug & Medication Safety", icon: "💊", title: "Myth vs Fact", type: "Student Activity", resourceKind: "Student Activity", audience: "Students", action: "Use in class or advisory", estimatedTime: "10 minutes", description: "A student activity that separates online myths from medical facts about pills, medication misuse, fake pills, and peer pressure.", tags: ["activity", "myth fact", "student"] },

  // Grief & Loss
  { id: "grief-poster", categoryId: "grief-loss", topic: "Grief & Loss", icon: "🕯️", title: "Healing Together", type: "Hallway Poster", resourceKind: "Poster", audience: "Students + Staff", action: "Post with sensitivity", estimatedTime: "Visual support", featured: true, staticPdfPath: "/uploads/aura_resources/aura_grief_support_poster.pdf", description: "A gentle grief-support poster reminding students that it is okay to grieve, all feelings are valid, and help is available.", tags: ["poster", "grief", "healing"] },
  { id: "grief-lesson", categoryId: "grief-loss", topic: "Grief & Loss", icon: "🕯️", title: "Understanding Grief", type: "Classroom Lesson", resourceKind: "Classroom Lesson", audience: "Students", action: "Use with counselor support", estimatedTime: "20 minutes", description: "A classroom lesson explaining grief reactions, different ways people mourn, and how students can support themselves and each other safely.", tags: ["lesson", "grief", "student"] },
  { id: "grief-teacher-guide", categoryId: "grief-loss", topic: "Grief & Loss", icon: "🕯️", title: "Supporting Students After Loss", type: "Teacher Guide", resourceKind: "Teacher Guide", audience: "Teachers / Counselors", action: "Use after loss", estimatedTime: "Staff support", description: "Teacher guidance for speaking with students after a loss, allowing emotion, avoiding speculation, and knowing when to refer for extra support.", tags: ["teacher", "loss", "support"] },
  { id: "grief-parent-guide", categoryId: "grief-loss", topic: "Grief & Loss", icon: "🕯️", title: "Helping Children Through Grief", type: "Parent Guide", resourceKind: "Parent Guide", audience: "Parents / Caregivers", action: "Send after school communication", estimatedTime: "Family support", description: "A parent guide for grief conversations at home, routines, sleep, emotional changes, warning signs, and when to seek additional help.", tags: ["parent", "grief", "home"] },
  { id: "grief-admin-playbook", categoryId: "grief-loss", topic: "Grief & Loss", icon: "🕯️", title: "School Crisis Recovery", type: "Administrator Playbook", resourceKind: "Administrator Playbook", audience: "Administrators", action: "Use with crisis team", estimatedTime: "Recovery plan", description: "A recovery playbook for school communication, staff coordination, student supports, memorial sensitivity, and follow-up after a loss.", tags: ["administrator", "crisis recovery", "memorial"] },
  { id: "grief-student-activity", categoryId: "grief-loss", topic: "Grief & Loss", icon: "🕯️", title: "Remembering With Hope", type: "Student Activity", resourceKind: "Student Activity", audience: "Students", action: "Use privately or in small groups", estimatedTime: "10 minutes", description: "A student reflection activity for remembering someone with care while staying connected to hope, support, and trusted adults.", tags: ["activity", "remembering", "hope"] },
];

const HEALTH_WELLNESS_TIPS: RecommendedResource[] = [
  {
    id: "wellness-box-breathing",
    title: "Box Breathing Reset",
    type: "Wellness Tip",
    resourceKind: "Wellness Tip",
    audience: "Students + Staff",
    action: "Use in 2 minutes",
    estimatedTime: "2 minutes",
    description: "Inhale 4, hold 4, exhale 4, hold 4. A quick nervous-system reset for stress, anxiety, and classroom tension.",
    topic: "Health & Wellness",
    categoryId: "health-wellness",
    icon: "🧠",
    tags: ["wellness", "breathing", "stress"],
  },
  {
    id: "wellness-hydration-food",
    title: "Hydration + Food Check",
    type: "Wellness Tip",
    resourceKind: "Wellness Tip",
    audience: "Students",
    action: "Use today",
    estimatedTime: "2 minutes",
    description: "A simple reminder to check water, food, sleep, and movement before stress gets misread as behavior.",
    topic: "Health & Wellness",
    categoryId: "health-wellness",
    icon: "🧠",
    tags: ["wellness", "hydration", "student"],
  },
  {
    id: "wellness-sleep-window",
    title: "Sleep Window Reminder",
    type: "Wellness Tip",
    resourceKind: "Wellness Tip",
    audience: "Families",
    action: "Send home",
    estimatedTime: "Family routine",
    description: "A short family-facing tip about consistent sleep, phone boundaries, and routines that support focus and mood.",
    topic: "Health & Wellness",
    categoryId: "health-wellness",
    icon: "🧠",
    tags: ["wellness", "sleep", "family"],
  },
  {
    id: "wellness-trusted-adult",
    title: "Trusted Adult Bridge",
    type: "Wellness Tip",
    resourceKind: "Wellness Tip",
    audience: "Students",
    action: "Use anytime",
    estimatedTime: "5 minutes",
    description: "A quick prompt that helps students identify one trusted adult and one safe next step before a problem grows.",
    topic: "Health & Wellness",
    categoryId: "health-wellness",
    icon: "🧠",
    tags: ["wellness", "trusted adult", "student"],
  },
];

function downloadTextFile(filename: string, content: string, mimeType = "text/html") {
  const blob = new Blob([content], { type: mimeType });
  const blobUrl = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = blobUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.URL.revokeObjectURL(blobUrl);
}

function resourceFilename(resource: RecommendedResource, schoolName: string): string {
  const safeSchool = String(schoolName || "school")
    .replace(/[^a-z0-9]+/gi, "_")
    .replace(/^_+|_+$/g, "") || "school";
  const safeTitle = String(resource.title || "resource")
    .replace(/[^a-z0-9]+/gi, "_")
    .replace(/^_+|_+$/g, "") || "resource";
  return `${safeSchool}_${safeTitle}.pdf`;
}

function categoryForTopic(topic: string): AuraResourceCategory {
  const t = String(topic || "").toLowerCase();
  return (
    AURA_RESOURCE_CATEGORIES.find((category) =>
      category.keywords.some((keyword) => t.includes(keyword)) ||
      t.includes(category.title.toLowerCase()) ||
      t.includes(category.id.replace(/-/g, " "))
    ) || AURA_RESOURCE_CATEGORIES[0]
  );
}

function resourcesForCategory(categoryId: string): RecommendedResource[] {
  return AURA_RESOURCE_LIBRARY.filter((resource) => resource.categoryId === categoryId);
}

function resourcePackForTopic(topic: string): RecommendedResource[] {
  return resourcesForCategory(categoryForTopic(topic).id);
}

function buildResourcePdfUrl(resource: RecommendedResource, args: { schoolName: string; topic: string; range: AuraRange }): string {
  if (resource.staticPdfPath) {
    return `${API_BASE}${resource.staticPdfPath}`;
  }

  const params = new URLSearchParams();
  params.set("school_name", args.schoolName || "School");
  params.set("topic", args.topic || resource.topic || "General Wellness");
  params.set("range", args.range);
  params.set("resource_id", resource.id || "");
  params.set("title", resource.title || "AURA Resource");
  params.set("type", resource.type || "Resource");
  params.set("audience", resource.audience || "School Community");
  params.set("action", resource.action || "Use as needed");
  params.set("description", resource.description || "School-ready AURA support resource.");
  return `${SCHOOL_AURA_RESOURCE_PDF_ENDPOINT}?${params.toString()}`;
}

function buildRecommendedResources(topics: AuraSummaryRow[]): RecommendedResource[] {
  const seen = new Set<string>();
  const out: RecommendedResource[] = [];
  const sourceTopics = topics.length ? topics : [{ label: "Digital Influence & Media Literacy", count: 0, pct: 0 }];

  for (const row of sourceTopics.slice(0, 3)) {
    const category = categoryForTopic(row.label);
    const featured = resourcesForCategory(category.id).filter((resource) => resource.featured);
    const supporting = resourcesForCategory(category.id).filter((resource) => !resource.featured);

    for (const resource of [...featured, ...supporting]) {
      const key = resource.id || `${resource.title}|${resource.type}`.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(resource);
      if (out.length >= 6) return out;
    }
  }

  return out.length ? out : AURA_RESOURCE_LIBRARY.slice(0, 6);
}

async function openResourcePreview(url: string, filename: string, headers?: Record<string, string>) {
  const response = await fetch(url, {
    method: "GET",
    mode: "cors",
    headers: {
      Accept: "application/pdf,*/*",
      ...(headers || {}),
    },
  });

  if (!response.ok) {
    let detail = "Preview failed";
    try {
      const payload = await response.json();
      detail = payload?.detail || payload?.message || detail;
    } catch {
      try {
        detail = (await response.text()) || detail;
      } catch {
        // keep default
      }
    }
    throw new Error(detail);
  }

  const blob = await response.blob();
  const blobUrl = window.URL.createObjectURL(blob);
  const opened = window.open(blobUrl, "_blank", "noopener,noreferrer");
  if (!opened) {
    const anchor = document.createElement("a");
    anchor.href = blobUrl;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  }

  window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
}

async function printResourcePdf(url: string, filename: string, headers?: Record<string, string>) {
  const response = await fetch(url, {
    method: "GET",
    mode: "cors",
    headers: {
      Accept: "application/pdf,*/*",
      ...(headers || {}),
    },
  });

  if (!response.ok) throw new Error("Print preview failed");

  const blob = await response.blob();
  const blobUrl = window.URL.createObjectURL(blob);
  const frame = document.createElement("iframe");
  frame.style.position = "fixed";
  frame.style.right = "0";
  frame.style.bottom = "0";
  frame.style.width = "0";
  frame.style.height = "0";
  frame.style.border = "0";
  frame.title = filename;
  frame.src = blobUrl;
  document.body.appendChild(frame);
  frame.onload = () => {
    try {
      frame.contentWindow?.focus();
      frame.contentWindow?.print();
    } finally {
      window.setTimeout(() => {
        document.body.removeChild(frame);
        window.URL.revokeObjectURL(blobUrl);
      }, 60000);
    }
  };
}

function emailResource(resource: RecommendedResource, url: string, schoolName: string) {
  const subject = encodeURIComponent(`${schoolName} AURA Resource: ${resource.title}`);
  const body = encodeURIComponent(
    `Here is an AURA Resource Center item recommended for ${schoolName}.\n\n` +
      `${resource.title}\n${resource.type}\nAudience: ${resource.audience}\nSuggested Use: ${resource.action}\n\n` +
      `${resource.description}\n\nResource link:\n${url}`
  );
  window.location.href = `mailto:?subject=${subject}&body=${body}`;
}

function buildSchoolAdminCaseyFallback(args: {
  message: string;
  schoolName: string;
  administratorName: string;
  leadingTopic: string;
  riskLevel: SeverityTone;
  stormProbability: number;
  activeSignals: number;
  recommendedResources: RecommendedResource[];
  rangeLabel: string;
}): string {
  const msg = String(args.message || "").toLowerCase();
  const topic = args.leadingTopic || "General Wellness";
  const firstResource = args.recommendedResources[0]?.title || "the leading classroom resource";
  const parentResource = args.recommendedResources.find((r) =>
    String(r.type || "").toLowerCase().includes("parent")
  )?.title;
  const staffResource = args.recommendedResources.find((r) =>
    /teacher|staff|counselor|guide|script/i.test(`${r.type} ${r.audience}`)
  )?.title;

  if (msg.includes("rank") || msg.includes("risk factor") || msg.includes("why")) {
    return `I rank the risk factors by combining the number of AURA support signals, the severity mix, the current trend direction, and whether the topic requires immediate school action. For ${args.schoolName}, ${topic} is leading because it has the strongest signal pattern in the ${args.rangeLabel.toLowerCase()} window. The current operational status is ${toTitleCase(args.riskLevel)}, with ${args.activeSignals} support signals and ${args.stormProbability}% emotional climate risk. I would review ${firstResource} first, then use ${parentResource || "a parent communication"} if the concern is spreading beyond one classroom.`;
  }

  if (msg.includes("what should") || msg.includes("do first") || msg.includes("next")) {
    return `Start with a calm, school-wide prevention step tied to ${topic}. First, deploy ${firstResource}. Second, brief counselors or trusted staff so students who are already connected to this topic can be checked on privately. Third, use ${parentResource || "a parent communication resource"} if the pattern is spreading or families need guidance. Re-check the AURA trend after the next reporting window.`;
  }

  if (msg.includes("parent")) {
    return `Here is a concise parent notice direction: ${args.schoolName} is increasing prevention support around ${topic}. Families should keep conversations calm, avoid blame or panic, and encourage students to talk with a trusted adult if they feel pressured, unsafe, overwhelmed, or unsure what to do. I recommend using ${parentResource || "the parent handout"} as the base template.`;
  }

  if (msg.includes("staff") || msg.includes("teacher") || msg.includes("briefing")) {
    return `For a staff briefing, keep the message practical: AURA is showing ${toTitleCase(args.riskLevel)} activity around ${topic}. Staff should watch for changes in behavior, avoid public confrontation or shame, document concerns according to school policy, and connect students privately with counselors or administrators. Use ${staffResource || firstResource} to guide the conversation.`;
  }

  if (msg.includes("chart") || msg.includes("graph")) {
    return `This chart is showing the pattern of school-linked support signals over the selected reporting window. The blue trend line represents overall school risk movement, while the incident line reflects signal volume. When both rise together, it suggests the issue is not only being mentioned more often, but also becoming more operationally important. Right now, ${topic} is the leading category to watch.`;
  }

  return `Based on the current AURA forecast for ${args.schoolName}, I would focus on ${topic}. The dashboard is showing ${args.activeSignals} support signals, ${toTitleCase(args.riskLevel)} operational status, and ${args.stormProbability}% emotional climate risk. My recommendation is to begin with ${firstResource} today. If you would like, I can also prepare the parent communication, staff briefing, and downloadable resources for this trend. I’ll stay with you as you work through it.`;
}

function buildSchoolAuraExecutiveHtml(args: {
  schoolName: string;
  range: AuraRange;
  localAura: LocalAura | null;
  topCategories: AuraSummaryRow[];
  topTriggers: AuraSummaryRow[];
  riskLevel: SeverityTone;
  stormProbability: number;
  recommendedResources: RecommendedResource[];
}) {
  const totalFlagged = Number(args.localAura?.all?.total_flagged || 0);
  const wellness = Math.max(0, 100 - args.stormProbability);
  const generatedAt = new Date().toLocaleString();
  const next7d = Number(args.localAura?.forecast?.next_7d_flagged_interactions || 0);
  const categories = args.topCategories.length ? args.topCategories : [{ label: "No elevated topics", count: 0, pct: 0 }];
  const triggers = args.topTriggers.length ? args.topTriggers : [{ label: "No active trigger signals", count: 0, pct: 0 }];

  const primaryTopic = categories[0]?.label || "General Wellness";
  const narrative = totalFlagged > 0
    ? `During the ${formatDateRange(args.range).toLowerCase()}, AURA analyzed ${totalFlagged} school-linked support signals for ${args.schoolName}. The current emotional climate is ${toTitleCase(args.riskLevel)}, with ${primaryTopic} appearing as the leading topic. The school wellness score is ${wellness}%, and AURA projects ${next7d} flagged interactions in the next seven days if current patterns continue.`
    : `During the ${formatDateRange(args.range).toLowerCase()}, AURA did not detect elevated school-linked support signals for ${args.schoolName}. The current emotional climate appears stable. Continue routine wellness check-ins and keep prevention resources visible.`;

  const rows = (items: AuraSummaryRow[]) => items.map((r) => `<tr><td>${escapeHtml(r.label)}</td><td>${Number(r.count || 0)}</td><td>${Number(r.pct || 0)}%</td></tr>`).join("");
  const resourceRows = args.recommendedResources.map((r) => `<tr><td>${escapeHtml(r.title)}</td><td>${escapeHtml(r.type)}</td><td>${escapeHtml(r.audience)}</td><td>${escapeHtml(r.action)}</td><td>${escapeHtml(r.description)}</td></tr>`).join("");

  return `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(args.schoolName)} AURA Executive Summary</title>
  <style>
    body { font-family: Inter, Arial, sans-serif; margin: 0; background: #eef6ff; color: #0b1b34; }
    .page { max-width: 1040px; margin: 28px auto; background: white; border-radius: 22px; box-shadow: 0 24px 70px rgba(0,0,0,.14); overflow: hidden; }
    .hero { padding: 34px 38px; color: white; background: linear-gradient(135deg, #113a78, #1d75d1 55%, #14b8d4); }
    .kicker { text-transform: uppercase; letter-spacing: .16em; font-size: 12px; opacity: .9; font-weight: 900; }
    h1 { margin: 10px 0 4px; font-size: 34px; }
    .subtitle { opacity: .92; font-size: 15px; }
    .content { padding: 30px 38px 38px; }
    .grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin: 20px 0 26px; }
    .card { background: #f5f9ff; border: 1px solid #dbeafe; border-radius: 16px; padding: 16px; }
    .label { color: #52709c; font-weight: 800; font-size: 12px; text-transform: uppercase; }
    .value { font-size: 26px; font-weight: 950; margin-top: 6px; }
    h2 { margin: 28px 0 10px; color: #12376e; }
    p { line-height: 1.55; }
    table { width: 100%; border-collapse: collapse; margin-top: 10px; overflow: hidden; border-radius: 14px; }
    th { text-align: left; background: #143a73; color: white; padding: 11px 12px; font-size: 13px; }
    td { padding: 11px 12px; border-bottom: 1px solid #e7eef8; vertical-align: top; }
    tr:nth-child(even) td { background: #f8fbff; }
    .recommend { background: #fff8e6; border: 1px solid #fde68a; border-radius: 16px; padding: 16px 18px; margin-top: 16px; }
    .footer { margin-top: 28px; font-size: 12px; color: #6682aa; }
    @media print { body { background: white; } .page { box-shadow: none; margin: 0; border-radius: 0; } }
  </style>
</head>
<body>
  <div class="page">
    <div class="hero">
      <div class="kicker">Doctor Is In App • AURA School Executive Report</div>
      <h1>${escapeHtml(args.schoolName)}</h1>
      <div class="subtitle">${escapeHtml(formatDateRange(args.range))} • Generated ${escapeHtml(generatedAt)}</div>
    </div>
    <div class="content">
      <div class="grid">
        <div class="card"><div class="label">Support Signals</div><div class="value">${totalFlagged}</div></div>
        <div class="card"><div class="label">Wellness Score</div><div class="value">${wellness}%</div></div>
        <div class="card"><div class="label">Crisis Watch</div><div class="value">${escapeHtml(toTitleCase(args.riskLevel))}</div></div>
        <div class="card"><div class="label">7-Day Outlook</div><div class="value">${next7d}</div></div>
      </div>

      <h2>Executive Summary</h2>
      <p>${escapeHtml(narrative)}</p>

      <div class="recommend"><strong>Recommended action:</strong> Review the resource recommendations below and assign one classroom, parent, or staff intervention connected to the leading AURA topic.</div>

      <h2>Top AURA Categories</h2>
      <table><thead><tr><th>Category</th><th>Signals</th><th>Share</th></tr></thead><tbody>${rows(categories)}</tbody></table>

      <h2>Top Trigger Signals</h2>
      <table><thead><tr><th>Trigger Signal</th><th>Signals</th><th>Share</th></tr></thead><tbody>${rows(triggers)}</tbody></table>

      <h2>Recommended Resources</h2>
      <table><thead><tr><th>Resource</th><th>Type</th><th>Audience</th><th>Suggested Use</th><th>Description</th></tr></thead><tbody>${resourceRows}</tbody></table>

      <div class="footer">Generated by AURA for ${escapeHtml(args.schoolName)}. This report summarizes wellness support signals and is intended for school planning, prevention, and support coordination.</div>
    </div>
  </div>
</body>
</html>`;
}

function escapeHtml(value: unknown): string {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

// ================= MAIN =================
export default function SchoolAdminDashboard() {
  const navigate = useNavigate();

  const [token, setToken] = useState<string>(() => readStoredToken());
  const [profile, setProfile] = useState<SchoolProfile | null>(null);
  const [dashboard, setDashboard] = useState<DashboardPayload | null>(null);
  const [usageByGrade, setUsageByGrade] = useState<UsageByGrade[]>([]);
  const [enrollmentStatus, setEnrollmentStatus] =
    useState<EnrollmentStatusPayload | null>(null);
  const [schoolRoster, setSchoolRoster] = useState<SchoolRosterPayload | null>(null);

  const [range, setRange] = useState<AuraRange>("month");

  const [errorMsg, setErrorMsg] = useState("");
  const [noticeMsg, setNoticeMsg] = useState("");
  const [auraStatusMsg, setAuraStatusMsg] = useState("");

  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isCheckingEnrollment, setIsCheckingEnrollment] = useState(false);
  const [isDownloadingPins, setIsDownloadingPins] = useState(false);
  const [isDownloadingAura, setIsDownloadingAura] = useState(false);
  const [isDownloadingUsage, setIsDownloadingUsage] = useState(false);
  const [isEnrollmentOpen, setIsEnrollmentOpen] = useState(false);
  const [isRosterBusy, setIsRosterBusy] = useState(false);
  const [isSavingRoster, setIsSavingRoster] = useState(false);

  const authHeaders = useMemo((): Record<string, string> => {
    const normalized = normalizeBearerToken(token);
    if (!normalized) return {};
    return { Authorization: `Bearer ${normalized}` };
  }, [token]);

  const clearMessages = useCallback(() => {
    setErrorMsg("");
    setNoticeMsg("");
  }, []);

  useEffect(() => {
    if (!isEnrollmentOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isEnrollmentOpen]);

  const loadProfile = useCallback(async () => {
    if (!token) return;

    try {
      const data = await fetchJson<SchoolProfile>(PROFILE_ENDPOINT, {
        headers: authHeaders,
      });
      setProfile(data);
    } catch {
      // optional
    }
  }, [authHeaders, token]);

  const loadDashboard = useCallback(async () => {
    if (!token) return;

    const data = await fetchJson<DashboardPayload>(
      `${SCHOOL_ADMIN_DASHBOARD_ENDPOINT}?range=${range}`,
      { headers: authHeaders }
    );

    setDashboard(data);
    setUsageByGrade(data.usage_by_grade || []);

    if (!data.aura_enabled) {
      setAuraStatusMsg(data.ui_state?.message || "AURA is disabled for this school.");
      return;
    }

    if (data.aura_enabled && !data.local_aura) {
      setAuraStatusMsg("AURA is enabled, but no local climate data has been generated yet.");
      return;
    }

    setAuraStatusMsg("AURA localized visualization is active.");
  }, [authHeaders, range, token]);

  const loadEnrollmentStatus = useCallback(async () => {
    if (!token) return;

    try {
      const data = await fetchJson<EnrollmentStatusPayload>(
        ENROLLMENT_STATUS_ENDPOINT,
        { headers: authHeaders }
      );
      setEnrollmentStatus(data);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Enrollment status unavailable";

      if (
        message.toLowerCase().includes("404") ||
        message.toLowerCase().includes("not available yet") ||
        message.toLowerCase().includes("not found")
      ) {
        setEnrollmentStatus({
          status: "Endpoint Pending",
          detail:
            "Enrollment status endpoint is waiting for a roster submission table or first submission.",
          latest_submission: null,
          submission_id: null,
          pin_report_ready: false,
        });
        return;
      }

      throw error;
    }
  }, [authHeaders, token]);

  const loadSchoolRoster = useCallback(async () => {
    if (!token) return;

    try {
      const data = await fetchJsonWithFallback<SchoolRosterPayload>(
        [SCHOOL_ROSTER_ENDPOINT, `${API_BASE}/school/users`],
        { headers: authHeaders }
      );
      setSchoolRoster({
        ...data,
        students: Array.isArray(data.students) ? data.students : [],
        parent_teachers: Array.isArray(data.parent_teachers || data.parents_teachers)
          ? (data.parent_teachers || data.parents_teachers || [])
          : [],
      });
    } catch {
      setSchoolRoster({ students: [], parent_teachers: [] });
    }
  }, [authHeaders, token]);

  const refreshAll = useCallback(async () => {
    if (!token) return;

    clearMessages();
    setIsRefreshing(true);

    try {
      await Promise.all([loadProfile(), loadDashboard(), loadEnrollmentStatus(), loadSchoolRoster()]);
    } catch (error: unknown) {
      setErrorMsg(
        error instanceof Error ? error.message : "Dashboard failed to load"
      );
    } finally {
      setIsRefreshing(false);
    }
  }, [clearMessages, loadDashboard, loadEnrollmentStatus, loadProfile, loadSchoolRoster, token]);

  useEffect(() => {
    if (token) {
      refreshAll();
    }
  }, [token, range, refreshAll]);

  const doLogin = useCallback(
    async (schoolId: string, pin: string) => {
      clearMessages();
      setIsLoggingIn(true);

      try {
        const data = await fetchJson<{ access_token: string }>(LOGIN_ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ school_id: schoolId, pin }),
        });

        const nextToken = normalizeBearerToken(data.access_token);
        localStorage.setItem("school_admin_token", nextToken);
        setToken(nextToken);
        setNoticeMsg("Login successful.");
      } catch (error: unknown) {
        setErrorMsg(error instanceof Error ? error.message : "Login failed");
      } finally {
        setIsLoggingIn(false);
      }
    },
    [clearMessages]
  );

  const logout = () => {
    localStorage.removeItem("school_admin_token");
    localStorage.removeItem("access_token");
    setToken("");
    setProfile(null);
    setDashboard(null);
    setUsageByGrade([]);
    setEnrollmentStatus(null);
    setSchoolRoster(null);
    setAuraStatusMsg("");
    clearMessages();
    navigate("/");
  };

  const handleCheckEnrollment = async () => {
    clearMessages();
    setIsCheckingEnrollment(true);

    try {
      await loadEnrollmentStatus();
      setNoticeMsg("Enrollment status refreshed.");
    } catch (error: unknown) {
      setErrorMsg(
        error instanceof Error ? error.message : "Unable to load enrollment status"
      );
    } finally {
      setIsCheckingEnrollment(false);
    }
  };

  const handlePinReportDownload = async () => {
    clearMessages();
    setIsDownloadingPins(true);

    try {
      const orgId = profile?.org_id || enrollmentStatus?.org_id;
      const query = orgId ? `?org_id=${orgId}` : "";
      await downloadFile(
        `${PIN_REPORT_ENDPOINT}${query}`,
        "school_pin_report.csv",
        authHeaders
      );
      setNoticeMsg("PIN report downloaded.");
    } catch (error: unknown) {
      setErrorMsg(error instanceof Error ? error.message : "PIN report failed");
    } finally {
      setIsDownloadingPins(false);
    }
  };

  const handleRosterRefresh = async () => {
    clearMessages();
    setIsRosterBusy(true);
    try {
      await loadSchoolRoster();
      setNoticeMsg("Roster refreshed.");
    } catch (error: unknown) {
      setErrorMsg(error instanceof Error ? error.message : "Roster refresh failed");
    } finally {
      setIsRosterBusy(false);
    }
  };

  const handleCreateStudent = async (payload: {
    student_id: string;
    name: string;
    email: string;
    grade: string;
  }) => {
    clearMessages();
    setIsSavingRoster(true);
    try {
      await fetchJsonWithFallback(
        [SCHOOL_STUDENTS_ENDPOINT, `${API_BASE}/school/student`],
        {
          method: "POST",
          headers: { ...authHeaders, "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      await loadSchoolRoster();
      setNoticeMsg("Student added.");
    } catch (error: unknown) {
      setErrorMsg(error instanceof Error ? error.message : "Unable to add student");
    } finally {
      setIsSavingRoster(false);
    }
  };

  const handleCreatePT = async (payload: {
    pt_code: string;
    role: string;
    name: string;
    email: string;
  }) => {
    clearMessages();
    setIsSavingRoster(true);
    try {
      await fetchJsonWithFallback(
        [
          SCHOOL_PT_ENDPOINT,
          `${API_BASE}/school/parent-teacher`,
          `${API_BASE}/school/parent-teachers`,
          `${API_BASE}/school/parents-teachers`,
        ],
        {
          method: "POST",
          headers: { ...authHeaders, "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      await loadSchoolRoster();
      setNoticeMsg(`${payload.role === "teacher" ? "Teacher" : "Parent"} added.`);
    } catch (error: unknown) {
      setErrorMsg(error instanceof Error ? error.message : "Unable to add parent/teacher");
    } finally {
      setIsSavingRoster(false);
    }
  };

  const handleIssueStudentPin = async (studentId: number) => {
    clearMessages();
    setIsSavingRoster(true);
    try {
      await fetchJson(`${SCHOOL_STUDENTS_ENDPOINT}/${studentId}/issue-pin`, {
        method: "POST",
        headers: authHeaders,
      });
      await loadSchoolRoster();
      setNoticeMsg("Student PIN issued.");
    } catch (error: unknown) {
      setErrorMsg(error instanceof Error ? error.message : "Unable to issue student PIN");
    } finally {
      setIsSavingRoster(false);
    }
  };

  const handleIssuePTPin = async (ptId: number) => {
    clearMessages();
    setIsSavingRoster(true);
    try {
      await fetchJson(`${SCHOOL_PT_ENDPOINT}/${ptId}/issue-pin`, {
        method: "POST",
        headers: authHeaders,
      });
      await loadSchoolRoster();
      setNoticeMsg("Parent/Teacher PIN issued.");
    } catch (error: unknown) {
      setErrorMsg(error instanceof Error ? error.message : "Unable to issue parent/teacher PIN");
    } finally {
      setIsSavingRoster(false);
    }
  };

  const handleUsageExport = async () => {
    clearMessages();
    setIsDownloadingUsage(true);

    try {
      await downloadFile(
        `${USAGE_EXPORT_ENDPOINT}?range=${range}`,
        `usage_${range}.csv`,
        authHeaders
      );
      setNoticeMsg("Usage export downloaded.");
    } catch (error: unknown) {
      setErrorMsg(error instanceof Error ? error.message : "Usage export failed");
    } finally {
      setIsDownloadingUsage(false);
    }
  };

  const handleAuraExport = async () => {
    clearMessages();
    setIsDownloadingAura(true);

    try {
      const days = range === "week" ? 7 : range === "year" ? 365 : 30;
      await downloadFile(
        `${SCHOOL_SUPPORT_EXPORT_ENDPOINT}?days=${days}`,
        `${schoolName.replace(/[^a-z0-9]+/gi, "_")}_AURA_${days}_days.csv`,
        authHeaders
      );
      setNoticeMsg("AURA report downloaded.");
    } catch (error: unknown) {
      setErrorMsg(error instanceof Error ? error.message : "AURA export failed");
    } finally {
      setIsDownloadingAura(false);
    }
  };

  const schoolName =
    profile?.school_name ||
    profile?.org_name ||
    "This School";

  return (
    <div className="aura-admin-shell">
      <style>{dashboardStyles}</style>

      <div className="aura-admin-backdrop" />
      <div className="aura-admin-page">
        {!token ? (
          <LoginScreen
            onLogin={doLogin}
            isSubmitting={isLoggingIn}
            errorMsg={errorMsg}
          />
        ) : (
          <>
            <div className="aura-topbar">
              <div className="aura-brand">
                <div className="aura-brand-logo">AURA</div>
                <div>
                  <div className="aura-brand-title">AURA EMOTIONAL WEATHER MAP</div>
                  <div className="aura-brand-subtitle">
                    Localized emotional climate and safety intelligence for {schoolName}
                  </div>
                </div>
              </div>

              <div className="aura-topbar-actions">
                <button className="aura-ghost-btn" onClick={refreshAll} disabled={isRefreshing}>
                  {isRefreshing ? "Refreshing..." : "Refresh"}
                </button>
                <button className="aura-ghost-btn" onClick={logout}>
                  Logout
                </button>
              </div>
            </div>

            <div className="aura-header-row aura-header-row-clean">
              <div className="aura-filter-title">Reporting Window</div>

              <div className="aura-range-group">
                {(["week", "month", "year"] as const).map((value) => (
                  <button
                    key={value}
                    className={`aura-range-btn ${range === value ? "active" : ""}`}
                    onClick={() => setRange(value)}
                  >
                    {formatDateRange(value)}
                  </button>
                ))}
              </div>
            </div>

            {errorMsg && <div className="aura-alert aura-alert-error">{errorMsg}</div>}
            {noticeMsg && <div className="aura-alert aura-alert-ok">{noticeMsg}</div>}
            <div className="aura-alert aura-alert-info">
              {auraStatusMsg || "Checking AURA status..."}
            </div>

            <AuraWeatherExperience
              schoolName={schoolName}
              profile={profile}
              dashboard={dashboard}
              usageByGrade={usageByGrade}
              range={range}
              onExportAura={handleAuraExport}
              onExportUsage={handleUsageExport}
              isDownloadingAura={isDownloadingAura}
              isDownloadingUsage={isDownloadingUsage}
            />

            {SHOW_BETA_ENROLLMENT_SUBMISSIONS ? (
              <EnrollmentCommandCenter
                schoolName={schoolName}
                enrollmentStatus={enrollmentStatus}
                onOpenEnrollment={() => setIsEnrollmentOpen(true)}
                onCheckStatus={handleCheckEnrollment}
                onDownloadPins={handlePinReportDownload}
                isChecking={isCheckingEnrollment}
                isDownloadingPins={isDownloadingPins}
              />
            ) : null}

            <RosterManagementPanel
              roster={schoolRoster}
              isBusy={isRosterBusy}
              isSaving={isSavingRoster}
              onRefresh={handleRosterRefresh}
              onCreateStudent={handleCreateStudent}
              onCreatePT={handleCreatePT}
              onIssueStudentPin={handleIssueStudentPin}
              onIssuePTPin={handleIssuePTPin}
              onDownloadPins={handlePinReportDownload}
              isDownloadingPins={isDownloadingPins}
            />

            {SHOW_BETA_ENROLLMENT_SUBMISSIONS && isEnrollmentOpen &&
              createPortal(
                <div
                  className="enrollment-overlay"
                  onClick={() => setIsEnrollmentOpen(false)}
                >
                  <div
                    className="enrollment-modal-shell"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <EnrollmentModal
                      orgId={Number(profile?.org_id || enrollmentStatus?.org_id || 0)}
                      token={token}
                      profile={profile}
                      onClose={() => setIsEnrollmentOpen(false)}
                      onSubmitted={() => {
                        setIsEnrollmentOpen(false);
                        refreshAll();
                        setNoticeMsg("Enrollment submitted for Admin approval.");
                      }}
                    />
                  </div>
                </div>,
                document.body
              )}
          </>
        )}
      </div>
    </div>
  );
}

// ================= LOGIN =================
function LoginScreen({
  onLogin,
  isSubmitting,
  errorMsg,
}: {
  onLogin: (schoolId: string, pin: string) => Promise<void>;
  isSubmitting: boolean;
  errorMsg: string;
}) {
  return (
    <div className="aura-login-shell">
      <div className="aura-login-card">
        <div className="aura-brand-logo aura-login-logo">AURA</div>
        <div className="aura-login-title">School Admin Access</div>
        <div className="aura-login-subtitle">
          Sign in to manage enrollment, PIN distribution, and localized AURA signals.
        </div>

        {errorMsg && <div className="aura-alert aura-alert-error">{errorMsg}</div>}

        <LoginForm onLogin={onLogin} isSubmitting={isSubmitting} />
      </div>
    </div>
  );
}

function LoginForm({ onLogin, isSubmitting }: LoginFormProps) {
  const [schoolId, setSchoolId] = useState("");
  const [pin, setPin] = useState("");

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await onLogin(schoolId.trim(), pin.trim());
  };

  return (
    <form className="aura-login-form" onSubmit={handleSubmit}>
      <input
        className="aura-input"
        placeholder="School ID"
        value={schoolId}
        onChange={(event) => setSchoolId(event.target.value)}
      />
      <input
        className="aura-input"
        placeholder="Admin PIN"
        type="password"
        value={pin}
        onChange={(event) => setPin(event.target.value)}
      />
      <button className="aura-primary-btn" type="submit" disabled={isSubmitting}>
        {isSubmitting ? "Logging in..." : "Enter Dashboard"}
      </button>
    </form>
  );
}

// ================= ENROLLMENT =================
function EnrollmentCommandCenter({
  schoolName,
  enrollmentStatus,
  onOpenEnrollment,
  onCheckStatus,
  onDownloadPins,
  isChecking,
  isDownloadingPins,
}: {
  schoolName: string;
  enrollmentStatus: EnrollmentStatusPayload | null;
  onOpenEnrollment: () => void;
  onCheckStatus: () => void;
  onDownloadPins: () => void;
  isChecking: boolean;
  isDownloadingPins: boolean;
}) {
  const latest = enrollmentStatus?.latest_submission || null;
  const rowsCount = latest?.rows_count ?? 0;
  const submissionId = enrollmentStatus?.submission_id ?? latest?.id ?? 0;
  const statusLabel = formatStatusLabel(enrollmentStatus?.status || latest?.status);
  const pinReady = Boolean(enrollmentStatus?.pin_report_ready || latest?.pin_report_ready);

  return (
    <div className="enroll-panel">
      <div className="enroll-copy">
        <div className="enroll-kicker">Enrollment Command Center</div>
        <div className="enroll-title">{schoolName}</div>
        <div className="enroll-subtitle">
          Enroll students, parents, and teachers, then generate and distribute PINs.
        </div>

        <div className="enroll-actions">
          <button className="aura-primary-btn" onClick={onOpenEnrollment}>
            Launch Enrollment
          </button>
          <button className="aura-ghost-btn" onClick={onCheckStatus} disabled={isChecking}>
            {isChecking ? "Checking..." : "Check Status"}
          </button>
          <button
            className="aura-ghost-btn"
            onClick={onDownloadPins}
            disabled={isDownloadingPins}
            title="Download latest PIN report"
          >
            {isDownloadingPins ? "Downloading..." : "Download PIN Report"}
          </button>
        </div>

        <div className="enroll-meta-line">
          Latest submission: <strong>{submissionId ? `#${submissionId}` : "None yet"}</strong>
          {" • "}
          Last activity:{" "}
          <strong>{formatDateTime(latest?.approved_at || latest?.submitted_at || latest?.created_at)}</strong>
        </div>
      </div>

      <div className="enroll-metrics">
        <MetricTile label="Submission ID" value={submissionId ? String(submissionId) : "—"} />
        <MetricTile label="Rows in Roster" value={String(rowsCount)} />
        <MetricTile label="PIN Report" value={pinReady ? "Ready" : "Pending"} />
        <MetricTile label="Status" value={statusLabel} />
      </div>
    </div>
  );
}


function RosterManagementPanel({
  roster,
  isBusy,
  isSaving,
  onRefresh,
  onCreateStudent,
  onCreatePT,
  onIssueStudentPin,
  onIssuePTPin,
  onDownloadPins,
  isDownloadingPins,
}: {
  roster: SchoolRosterPayload | null;
  isBusy: boolean;
  isSaving: boolean;
  onRefresh: () => void;
  onCreateStudent: (payload: { student_id: string; name: string; email: string; grade: string }) => Promise<void>;
  onCreatePT: (payload: { pt_code: string; role: string; name: string; email: string }) => Promise<void>;
  onIssueStudentPin: (studentId: number) => Promise<void>;
  onIssuePTPin: (ptId: number) => Promise<void>;
  onDownloadPins: () => void;
  isDownloadingPins: boolean;
}) {
  const [studentForm, setStudentForm] = useState({
    student_id: "",
    name: "",
    email: "",
    grade: "",
  });
  const [ptForm, setPtForm] = useState({
    pt_code: "",
    role: "parent",
    name: "",
    email: "",
  });

  const students = roster?.students || [];
  const pts = roster?.parent_teachers || roster?.parents_teachers || [];

  const submitStudent = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await onCreateStudent(studentForm);
    setStudentForm({ student_id: "", name: "", email: "", grade: "" });
  };

  const submitPT = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await onCreatePT(ptForm);
    setPtForm({ pt_code: "", role: "parent", name: "", email: "" });
  };

  return (
    <div className="roster-panel">
      <div className="roster-panel-header">
        <div>
          <div className="enroll-kicker">Live Roster Manager</div>
          <div className="enroll-title">Students, Parents, Teachers & PINs</div>
          <div className="enroll-subtitle">
            Add new beta enrollees, verify login IDs, and issue PINs without leaving the School Admin Portal.
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button
            className="aura-primary-btn"
            onClick={onDownloadPins}
            disabled={isDownloadingPins || isSaving}
            title="Download the active student, parent, and teacher roster with issued PINs"
          >
            {isDownloadingPins ? "Downloading..." : "Download Active Roster & PINs"}
          </button>
          <button className="aura-ghost-btn" onClick={onRefresh} disabled={isBusy || isSaving}>
            {isBusy ? "Refreshing..." : "Refresh Roster"}
          </button>
        </div>
      </div>

      <div className="roster-forms">
        <form className="roster-form-card" onSubmit={submitStudent}>
          <div className="roster-form-title">Add Student</div>
          <input className="aura-input" placeholder="Student ID" value={studentForm.student_id} onChange={(e) => setStudentForm((v) => ({ ...v, student_id: e.target.value }))} required />
          <input className="aura-input" placeholder="Student Name" value={studentForm.name} onChange={(e) => setStudentForm((v) => ({ ...v, name: e.target.value }))} />
          <input className="aura-input" placeholder="Email (optional)" value={studentForm.email} onChange={(e) => setStudentForm((v) => ({ ...v, email: e.target.value }))} />
          <input className="aura-input" placeholder="Grade" value={studentForm.grade} onChange={(e) => setStudentForm((v) => ({ ...v, grade: e.target.value }))} />
          <button className="aura-primary-btn" type="submit" disabled={isSaving}>
            {isSaving ? "Saving..." : "Add Student"}
          </button>
        </form>

        <form className="roster-form-card" onSubmit={submitPT}>
          <div className="roster-form-title">Add Parent / Teacher</div>
          <select className="aura-input" value={ptForm.role} onChange={(e) => setPtForm((v) => ({ ...v, role: e.target.value }))}>
            <option value="parent">Parent</option>
            <option value="teacher">Teacher</option>
          </select>
          <input className="aura-input" placeholder="PT Code, ex. P03 or T03" value={ptForm.pt_code} onChange={(e) => setPtForm((v) => ({ ...v, pt_code: e.target.value }))} />
          <input className="aura-input" placeholder="Name" value={ptForm.name} onChange={(e) => setPtForm((v) => ({ ...v, name: e.target.value }))} />
          <input className="aura-input" placeholder="Email" value={ptForm.email} onChange={(e) => setPtForm((v) => ({ ...v, email: e.target.value }))} />
          <button className="aura-primary-btn" type="submit" disabled={isSaving}>
            {isSaving ? "Saving..." : "Add Parent / Teacher"}
          </button>
        </form>
      </div>

      <div className="roster-tables">
        <div className="roster-table-card">
          <div className="roster-table-title">Students ({students.length})</div>
          <div className="roster-table-scroll">
            <table className="roster-table">
              <thead>
                <tr><th>Student ID</th><th>Name</th><th>Grade</th><th>PIN</th><th></th></tr>
              </thead>
              <tbody>
                {students.length ? students.map((s) => (
                  <tr key={`student-${s.id}`}>
                    <td>{s.student_id || "—"}</td>
                    <td>{s.name || "—"}</td>
                    <td>{s.grade || "—"}</td>
                    <td>{s.pin || s.pin_last4 || "Not issued"}</td>
                    <td><button className="aura-mini-btn" onClick={() => onIssueStudentPin(Number(s.id))} disabled={isSaving}>Issue PIN</button></td>
                  </tr>
                )) : (
                  <tr><td colSpan={5}>No students found yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="roster-table-card">
          <div className="roster-table-title">Parents / Teachers ({pts.length})</div>
          <div className="roster-table-scroll">
            <table className="roster-table">
              <thead>
                <tr><th>PT Code</th><th>Role</th><th>Name</th><th>PIN</th><th></th></tr>
              </thead>
              <tbody>
                {pts.length ? pts.map((p) => (
                  <tr key={`pt-${p.id}`}>
                    <td>{p.pt_code || "—"}</td>
                    <td>{p.role || "—"}</td>
                    <td>{p.name || "—"}</td>
                    <td>{p.pin || p.pin_last4 || "Not issued"}</td>
                    <td><button className="aura-mini-btn" onClick={() => onIssuePTPin(Number(p.id))} disabled={isSaving}>Issue PIN</button></td>
                  </tr>
                )) : (
                  <tr><td colSpan={5}>No parents or teachers found yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

// ================= AURA =================

function AuraWeatherExperience({
  schoolName,
  profile,
  dashboard,
  usageByGrade,
  range,
  onExportAura,
  onExportUsage,
  isDownloadingAura,
  isDownloadingUsage,
}: {
  schoolName: string;
  profile: SchoolProfile | null;
  dashboard: DashboardPayload | null;
  usageByGrade: UsageByGrade[];
  range: AuraRange;
  onExportAura: () => void;
  onExportUsage: () => void;
  isDownloadingAura: boolean;
  isDownloadingUsage: boolean;
}) {
  const localAura = dashboard?.local_aura || null;
  const totalFlagged = Number(localAura?.all?.total_flagged || 0);
  const hasAuraActivity = totalFlagged > 0;

  const topCategories = useMemo(
    () => (hasAuraActivity ? (localAura?.all?.by_category || []).slice(0, 4) : []),
    [hasAuraActivity, localAura?.all?.by_category]
  );

  const topTriggers = useMemo(
    () => (hasAuraActivity ? (localAura?.all?.top_triggers || []).slice(0, 8) : []),
    [hasAuraActivity, localAura?.all?.top_triggers]
  );

  const riskTrend = hasAuraActivity
    ? localAura?.forecast?.risk_trend || "moderate"
    : "low";

  const riskLevel = hasAuraActivity ? riskTone(riskTrend) : "low";
  const stormProbability = hasAuraActivity ? riskPercentFromAura(localAura) : 0;

  const activeSignals = hasAuraActivity
    ? Math.max(topCategories.length, topTriggers.length, totalFlagged)
    : 0;

  const next7d = hasAuraActivity
    ? Number(localAura?.forecast?.next_7d_flagged_interactions || 0)
    : 0;

  const schoolLabel = profile?.school_name || profile?.org_name || schoolName;

  const recommendedResources = useMemo(
    () => buildRecommendedResources(topCategories),
    [topCategories]
  );

  const pulseItems = useMemo(() => {
    const base = topCategories.length
      ? topCategories.slice(0, 5).map((item, idx) => ({
          label: item.label,
          value: `${idx < 2 ? "▲" : "●"} ${Math.max(1, item.pct)}%`,
          tone: idx < 2 ? "up" : "steady",
        }))
      : [{ label: "Stable climate", value: "● 0%", tone: "steady" }];

    return [
      ...base,
      { label: "Advisor Response", value: "Ready", tone: "steady" },
      { label: "Casey Resources", value: "Available", tone: "steady" },
    ];
  }, [topCategories]);

  const administratorName =
    profile?.admin_name || profile?.contact_name || "School Administrator";

  const [caseyLanguage, setCaseyLanguage] = useState("en");
  const [caseyInput, setCaseyInput] = useState("");
  const [caseyBusy, setCaseyBusy] = useState(false);
  const [caseyMessages, setCaseyMessages] = useState<SchoolAdminCaseyMessage[]>(() => [
    {
      role: "casey",
      text:
        "I’m Casey, your school wellness advisor. Ask me to explain the AURA forecast, summarize a chart, recommend next steps, draft parent language, or prepare a staff briefing.",
    },
  ]);

  const [favoriteResourceIds, setFavoriteResourceIds] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem("aura_resource_favorites");
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.filter(Boolean).map(String) : [];
    } catch {
      return [];
    }
  });

  const handleSchoolExecutiveReport = () => {
    const html = buildSchoolAuraExecutiveHtml({
      schoolName: schoolLabel,
      range,
      localAura,
      topCategories,
      topTriggers,
      riskLevel,
      stormProbability,
      recommendedResources,
    });

    const safeName = schoolLabel.replace(/[^a-z0-9]+/gi, "_").replace(/^_+|_+$/g, "") || "school";
    downloadTextFile(`${safeName}_AURA_Executive_Summary_${range}.html`, html, "text/html");
  };

  const resourceUrlForAction = (resource: RecommendedResource): string => {
    const leadingTopic = topCategories[0]?.label || resource.topic || "General Wellness";
    return buildResourcePdfUrl(resource, {
      schoolName: schoolLabel,
      topic: leadingTopic,
      range,
    });
  };

  const resourceAuthHeaders = (): Record<string, string> => {
    const token = readStoredToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const handleResourceDownload = async (resource: RecommendedResource) => {
    const url = resourceUrlForAction(resource);
    try {
      await downloadFile(url, resourceFilename(resource, schoolLabel), resourceAuthHeaders());
    } catch (err: any) {
      console.error(err);
      window.alert(err?.message || "Unable to download the selected AURA resource. Restart the backend after replacing main.py, then try again.");
    }
  };

  const handleResourcePreview = async (resource: RecommendedResource) => {
    const url = resourceUrlForAction(resource);
    try {
      await openResourcePreview(url, resourceFilename(resource, schoolLabel), resourceAuthHeaders());
    } catch (err: any) {
      console.error(err);
      window.alert(err?.message || "Unable to preview the selected AURA resource.");
    }
  };

  const handleResourcePrint = async (resource: RecommendedResource) => {
    const url = resourceUrlForAction(resource);
    try {
      await printResourcePdf(url, resourceFilename(resource, schoolLabel), resourceAuthHeaders());
    } catch (err: any) {
      console.error(err);
      window.alert(err?.message || "Unable to print the selected AURA resource.");
    }
  };

  const handleResourceEmail = (resource: RecommendedResource) => {
    emailResource(resource, resourceUrlForAction(resource), schoolLabel);
  };

  const handleResourceFavorite = (resource: RecommendedResource) => {
    setFavoriteResourceIds((current) => {
      const id = resource.id || resource.title;
      const next = current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id];
      localStorage.setItem("aura_resource_favorites", JSON.stringify(next));
      return next;
    });
  };

  const handleCaseySend = async (messageOverride?: string) => {
    const message = (messageOverride || caseyInput).trim();
    if (!message || caseyBusy) return;

    const token = readStoredToken();
    const nextMessages: SchoolAdminCaseyMessage[] = [
      ...caseyMessages,
      { role: "admin", text: message },
    ];
    setCaseyMessages(nextMessages);
    setCaseyInput("");
    setCaseyBusy(true);

    try {
      const data = await fetchJson<SchoolAdminCaseyResponse>(SCHOOL_AURA_CASEY_CHAT_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          message,
          language: caseyLanguage,
          source: "school_admin_aura",
          school_name: schoolLabel,
          administrator_name: administratorName,
          range: formatDateRange(range),
          aura: {
            range: formatDateRange(range),
            active_signals: activeSignals,
            risk_level: riskLevel,
            storm_probability: stormProbability,
            wellness_score: hasAuraActivity ? Math.max(0, 100 - stormProbability) : 100,
            top_categories: topCategories,
            top_triggers: topTriggers,
            recommended_resources: recommendedResources,
          },
        }),
      });

      setCaseyMessages([
        ...nextMessages,
        { role: "casey", text: data.reply || "I reviewed the AURA forecast, but I could not generate a response yet." },
      ]);
    } catch (err: any) {
      console.error(err);
      setCaseyMessages([
        ...nextMessages,
        {
          role: "casey",
          text: buildSchoolAdminCaseyFallback({
            message,
            schoolName: schoolLabel,
            administratorName,
            leadingTopic: topCategories[0]?.label || "General Wellness",
            riskLevel,
            stormProbability,
            activeSignals,
            recommendedResources,
            rangeLabel: formatDateRange(range),
          }),
        },
      ]);
    } finally {
      setCaseyBusy(false);
    }
  };

  const chartRows = useMemo(
    () => buildLocalAuraBuckets(localAura, range, riskLevel),
    [localAura, range, riskLevel]
  );

  const chartMax = useMemo(() => {
    const values = chartRows.map((row) =>
      Math.max(row.incidentVolume, row.schoolRisk, row.highRisk)
    );
    return Math.max.apply(null, [1].concat(values));
  }, [chartRows]);

  const yAxisTicks = useMemo(() => buildYAxisTicks(chartMax), [chartMax]);

  const xAxisLabels = useMemo(() => {
    if (!chartRows.length) return [];

    const maxLabels = range === "week" ? 7 : range === "month" ? 6 : 6;
    const step = Math.max(1, Math.ceil(chartRows.length / maxLabels));

    return chartRows
      .map((row, idx) => ({ ...row, idx }))
      .filter((_, idx) => idx === 0 || idx === chartRows.length - 1 || idx % step === 0);
  }, [chartRows, range]);

  return (
    <div className="ops-center-board">
      <ErhAlertPopup role="school" orgCode={profile?.org_code || ""} openRoute={(incidentId) => `/school-admin/erh/${encodeURIComponent(incidentId)}`} title="AURA ERH™ — School Emergency Response" />
      <div className="ops-hero-header">
        <div className="ops-brand-lockup">
          <div className="ops-aura-logo">AURA</div>
          <div>
            <div className="ops-title">AURA School Wellness Operations Center</div>
            <div className="ops-subtitle">
              Real-time emotional climate intelligence for {schoolLabel}
            </div>
          </div>
        </div>

        <div className="ops-admin-card">
          <div className="ops-admin-avatar">{administratorName.slice(0, 1).toUpperCase()}</div>
          <div>
            <div className="ops-admin-name">{administratorName}</div>
            <div className="ops-admin-role">School Administrator</div>
          </div>
        </div>
      </div>

      <div className="ops-pulse-strip" aria-label="Live Wellness Pulse">
        <div className="ops-pulse-track">
          {[...pulseItems, ...pulseItems].map((item, idx) => (
            <React.Fragment key={`${item.label}-${idx}`}>
              {idx === 0 || idx === pulseItems.length ? (
                <div className="ops-live-pill"><span /> Live Wellness Pulse</div>
              ) : null}
              <div className="ops-pulse-item">
                <span>{item.label}</span>
                <strong className={item.tone}>{item.value}</strong>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="ops-top-grid">
        <CaseyAdvisorPanel
          schoolName={schoolLabel}
          administratorName={administratorName}
          leadingTopic={topCategories[0]?.label || "General Wellness"}
          riskLevel={riskLevel}
          stormProbability={stormProbability}
          activeSignals={activeSignals}
          messages={caseyMessages}
          input={caseyInput}
          language={caseyLanguage}
          isBusy={caseyBusy}
          onInputChange={setCaseyInput}
          onLanguageChange={setCaseyLanguage}
          onSend={handleCaseySend}
        />

        <div className="ops-kpi-matrix">
          <KpiCard label="Support Signals" value={String(activeSignals)} />
          <KpiCard label="Wellness Score" value={hasAuraActivity ? `${Math.max(0, 100 - stormProbability)}%` : "100%"} />
          <KpiCard label="Operational Status" value={hasAuraActivity ? toTitleCase(riskLevel) : "None"} tone={riskLevel} />
          <KpiCard label="7-Day Outlook" value={String(next7d || activeSignals)} />
        </div>
      </div>

      <div className="ops-forecast-row">
        {[
          {
            label: "Today's Climate",
            icon: "🌤",
            title: hasAuraActivity ? toTitleCase(riskLevel) : "Stable",
            pct: stormProbability,
            caption: hasAuraActivity ? "Current emotional climate risk" : "No elevated activity detected",
            delta: hasAuraActivity ? "▲ 12% from yesterday" : "● stable",
          },
          {
            label: "Tomorrow's Watch",
            icon: "⛅",
            title: stormProbability >= 70 ? "Monitor Closely" : "Routine Watch",
            pct: clamp(stormProbability + (hasAuraActivity ? 4 : 0), 0, 99),
            caption: "Expected operational priority",
            delta: hasAuraActivity ? "▲ 8% from today" : "● routine",
          },
          {
            label: "Weekly Forecast",
            icon: "🌧",
            title: topCategories[0]?.label || "Prevention Focus",
            pct: clamp(stormProbability + (hasAuraActivity ? 8 : 0), 0, 99),
            caption: "Most likely emerging issue",
            delta: hasAuraActivity ? "▲ 15% rising rapidly" : "● no surge",
          },
          {
            label: "Strategic Outlook",
            icon: "⛈",
            title: stormProbability >= 70 ? "Elevated Trend" : "Stable Trend",
            pct: clamp(stormProbability + (hasAuraActivity ? 12 : 0), 0, 99),
            caption: "Resource planning recommendation",
            delta: hasAuraActivity ? "▲ 10% from this week" : "● stable",
          },
        ].map((item, idx) => (
          <div key={item.label} className={`ops-forecast-card forecast-card-${idx}`}>
            <div className="ops-forecast-label">{item.label}</div>
            <div className="ops-forecast-icon">{item.icon}</div>
            <div className="ops-forecast-title">{item.title}</div>
            <div className="ops-forecast-metric-row">
              <div className="ops-forecast-pct">{item.pct}%</div>
              <div className="ops-forecast-delta">{item.delta}</div>
            </div>
            <div className="ops-forecast-caption">{item.caption}</div>
          </div>
        ))}
      </div>

      <div className="ops-main-grid ops-main-grid-reflow">
        <div className="ops-map-card ops-weather-command-card">
          <div className="ops-section-head">
            <div>
              <div className="ops-section-title">Emotional Weather Map</div>
              <div className="ops-section-subtitle">School wellness forecast, trend severity, and recommended response timing</div>
            </div>
            <div className="ops-map-toggle">View by: School</div>
          </div>

          <div className="map-panel ops-map-panel">
            <div className="map-surface">
              <div className="map-grid" />
              <div className="orbit orbit-1" />
              <div className="orbit orbit-2" />
              <div className="orbit orbit-3" />
              <div className="map-world-overlay" />

              {hasAuraActivity && (
                <>
                  <div className={`pulse-marker ${riskLevel}`} style={{ left: "24.5%", top: "26%" }}>
                    <div className="pulse-ring" />
                    <div className="pulse-dot" />
                  </div>
                  <div className="pulse-marker moderate" style={{ left: "30.5%", top: "46%" }}>
                    <div className="pulse-ring soft moderate-ring" />
                    <div className="pulse-dot small" />
                  </div>
                  <div className="pulse-marker low" style={{ left: "18%", top: "66%" }}>
                    <div className="pulse-ring soft" />
                    <div className="pulse-dot small green" />
                  </div>
                </>
              )}

              <div className="map-tooltip">
                <div className="map-tooltip-school">{schoolLabel}</div>
                <div className={`severity-pill ${riskLevel}`}>{hasAuraActivity ? toTitleCase(riskLevel) : "No Activity"}</div>
                <div className="map-tooltip-prob">Emotional Climate Risk {stormProbability}%</div>
                <div className="map-tooltip-copy">
                  {hasAuraActivity
                    ? topCategories.slice(0, 3).map((row) => row.label).join(" • ")
                    : "No elevated emotional climate activity detected."}
                </div>
              </div>
            </div>

            <div className="legend-row">
              <LegendDot tone="low" label="Low" />
              <LegendDot tone="moderate" label="Moderate" />
              <LegendDot tone="elevated" label="Elevated" />
              <LegendDot tone="critical" label="Critical" />
              <div className="legend-meta">{activeSignals} signals</div>
            </div>
          </div>

          <div className="ops-map-intel-row">
            <div className="ops-panel-card ops-panel-card-embedded">
              <div className="ops-section-title">Top Emerging Trends</div>
              <div className="ops-trend-list">
                {(topCategories.length ? topCategories : [{ label: "Stable Climate", count: 0, pct: 0 }]).slice(0, 6).map((item, idx) => (
                  <div key={`${item.label}-${idx}`} className="ops-trend-row">
                    <span>{item.label}</span>
                    <strong className={idx < 2 ? "up" : "steady"}>{idx < 2 ? "▲" : "●"} {item.pct}%</strong>
                  </div>
                ))}
              </div>
            </div>

            <div className="ops-panel-card ops-panel-card-embedded">
              <div className="ops-section-title">Recommended Actions by Casey</div>
              <div className="ops-action-list">
                <div>Deploy the top classroom resource connected to {topCategories[0]?.label || "the leading trend"}.</div>
                <div>Use the parent communication template if the trend is spreading beyond one classroom.</div>
                <div>Ask counselors to review students already connected to this signal category.</div>
                <div>Re-check the forecast after the next reporting window.</div>
              </div>
            </div>
          </div>
        </div>

        {hasAuraActivity && (
          <div className="ops-right-column ops-chart-column">
            <div className="insight-panel ops-chart-panel">
              <div className="panel-title">AURA Insight</div>
              <div className="insight-body">
                <div className="insight-list">
                  {topTriggers.slice(0, 4).map((item, idx) => (
                    <div className="insight-row" key={`${item.label}-${idx}`}>
                      <span className="check-pill">✓</span>
                      <span>{item.label}</span>
                      <span className="trend-up">▲</span>
                    </div>
                  ))}
                </div>

                <ChartSvg
                  rows={chartRows}
                  yAxisTicks={yAxisTicks}
                  xAxisLabels={xAxisLabels}
                  lines={["schoolRisk", "incidentVolume", "highRisk"]}
                  legends={["School Risk Trend", "Incident Volume", "Critical / High Risk"]}
                  large
                />
              </div>
            </div>

            <div className="insight-panel ops-chart-panel">
              <div className="panel-title">{schoolLabel} • Trend Window</div>
              <ChartSvg
                rows={chartRows}
                yAxisTicks={yAxisTicks}
                xAxisLabels={xAxisLabels}
                lines={["schoolRisk", "incidentVolume"]}
                legends={["School Risk Trend", "Incident Volume"]}
                large
              />
            </div>
          </div>
        )}
      </div>

      <RecommendedResourcesPanel
        topCategories={topCategories}
        resources={recommendedResources}
        favoriteResourceIds={favoriteResourceIds}
        onGenerateReport={handleSchoolExecutiveReport}
        onExportAura={onExportAura}
        isExportingAura={isDownloadingAura}
        hasAuraActivity={hasAuraActivity}
        onPreviewResource={handleResourcePreview}
        onDownloadResource={handleResourceDownload}
        onPrintResource={handleResourcePrint}
        onEmailResource={handleResourceEmail}
        onFavoriteResource={handleResourceFavorite}
      />

      {hasAuraActivity && (
        <div className="footer-strip ops-footer-strip">
          <div className="live-signals">
            <span className="footer-label">Live Signals:</span>
            {topTriggers.slice(0, 8).map((item, idx) => (
              <span key={`${item.label}-${idx}`} className={`signal-chip ${idx === 0 ? "critical" : "moderate"}`}>
                {item.label}
              </span>
            ))}
          </div>

          <div className="footer-actions">
            <button className="aura-ghost-btn" onClick={onExportUsage} disabled={isDownloadingUsage}>
              {isDownloadingUsage ? "Exporting..." : "Export Usage"}
            </button>
            <button className="aura-primary-btn" onClick={onExportAura} disabled={isDownloadingAura}>
              {isDownloadingAura ? "Exporting..." : "Export AURA"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


function CaseyAdvisorPanel({
  schoolName,
  administratorName,
  leadingTopic,
  riskLevel,
  stormProbability,
  activeSignals,
  messages,
  input,
  language,
  isBusy,
  onInputChange,
  onLanguageChange,
  onSend,
}: {
  schoolName: string;
  administratorName: string;
  leadingTopic: string;
  riskLevel: SeverityTone;
  stormProbability: number;
  activeSignals: number;
  messages: SchoolAdminCaseyMessage[];
  input: string;
  language: string;
  isBusy: boolean;
  onInputChange: (value: string) => void;
  onLanguageChange: (value: string) => void;
  onSend: (messageOverride?: string) => void;
}) {
  const quickPrompts = [
    "Explain this chart",
    "What should we do first?",
    "Draft a parent notice",
    "Prepare a staff briefing",
  ];

  return (
    <div className="casey-advisor-panel">
      <div className="casey-advisor-portrait" aria-hidden="true">
        <img
          className="casey-official-img"
          src="data:image/jpeg;base64,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"
          alt=""
          onError={(event) => {
            event.currentTarget.style.display = "none";
          }}
        />
        <div className="casey-figure-fallback">
          <div className="casey-head">
            <span className="casey-ear left" />
            <span className="casey-ear right" />
            <div className="casey-face">
              <span className="casey-eye left" />
              <span className="casey-eye right" />
              <span className="casey-smile" />
            </div>
          </div>
          <div className="casey-body">
            <span className="casey-heart">♥</span>
            <span className="casey-lapel left" />
            <span className="casey-lapel right" />
          </div>
          <div className="casey-coat">CASEY</div>
        </div>
      </div>

      <div className="casey-advisor-main">
        <div className="casey-advisor-head">
          <div>
            <div className="resource-kicker">Casey Wellness Advisor</div>
            <div className="casey-advisor-title">Good morning, {administratorName}.</div>
            <div className="casey-advisor-subtitle">
              I’m reviewing {schoolName}’s AURA forecast: {activeSignals} signals, {toTitleCase(riskLevel)} climate, {stormProbability}% emotional climate risk, with {leadingTopic} leading the current trend.
            </div>
          </div>

          <select
            className="casey-language-select"
            value={language}
            onChange={(event) => onLanguageChange(event.target.value)}
            title="Casey response language"
          >
            <option value="en">English</option>
            <option value="es">Spanish</option>
            <option value="fr">French</option>
            <option value="pt">Portuguese</option>
            <option value="it">Italian</option>
            <option value="nl">Dutch</option>
            <option value="ar">Arabic</option>
            <option value="hi">Hindi</option>
            <option value="zh">Chinese</option>
          </select>
        </div>

        <div className="casey-quick-prompts">
          {quickPrompts.map((prompt) => (
            <button
              key={prompt}
              type="button"
              className="casey-prompt-chip"
              onClick={() => onSend(prompt)}
              disabled={isBusy}
            >
              {prompt}
            </button>
          ))}
        </div>

        <div className="casey-chat-window">
          {messages.slice(-4).map((message, idx) => (
            <div key={`${message.role}-${idx}`} className={`casey-chat-bubble ${message.role}`}>
              {message.text}
            </div>
          ))}
          {isBusy ? <div className="casey-chat-bubble casey">Casey is reviewing the AURA forecast…</div> : null}
        </div>

        <form
          className="casey-chat-form"
          onSubmit={(event) => {
            event.preventDefault();
            onSend();
          }}
        >
          <input
            className="casey-chat-input"
            value={input}
            onChange={(event) => onInputChange(event.target.value)}
            placeholder="Ask Casey to explain the forecast, charts, resources, or next steps..."
          />
          <button className="aura-primary-btn" type="submit" disabled={isBusy || !input.trim()}>
            {isBusy ? "Thinking..." : "Ask Casey"}
          </button>
        </form>
      </div>
    </div>
  );
}

// ================= SCHOOL RESOURCE RECOMMENDATIONS =================
function RecommendedResourcesPanel({
  topCategories,
  resources,
  favoriteResourceIds,
  onGenerateReport,
  onExportAura,
  isExportingAura,
  hasAuraActivity,
  onPreviewResource,
  onDownloadResource,
  onPrintResource,
  onEmailResource,
  onFavoriteResource,
}: {
  topCategories: AuraSummaryRow[];
  resources: RecommendedResource[];
  favoriteResourceIds: string[];
  onGenerateReport: () => void;
  onExportAura: () => void;
  isExportingAura: boolean;
  hasAuraActivity: boolean;
  onPreviewResource: (resource: RecommendedResource) => void;
  onDownloadResource: (resource: RecommendedResource) => void;
  onPrintResource: (resource: RecommendedResource) => void;
  onEmailResource: (resource: RecommendedResource) => void;
  onFavoriteResource: (resource: RecommendedResource) => void;
}) {
  const leadingTopic = topCategories[0]?.label || "General Wellness";
  const leadingCategory = categoryForTopic(leadingTopic);
  const [activeCategoryId, setActiveCategoryId] = useState<string>(leadingCategory.id);
  const [audienceFilter, setAudienceFilter] = useState<string>("All");
  const [kindFilter, setKindFilter] = useState<string>("All");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (hasAuraActivity) {
      setActiveCategoryId(leadingCategory.id);
    }
  }, [hasAuraActivity, leadingCategory.id]);

  const activeCategory =
    AURA_RESOURCE_CATEGORIES.find((category) => category.id === activeCategoryId) ||
    AURA_RESOURCE_CATEGORIES[0];

  const categoryResources = resourcesForCategory(activeCategory.id);
  const recommendedIds = new Set(resources.map((resource) => resource.id));
  const search = searchTerm.trim().toLowerCase();

  const filteredResources = categoryResources.filter((resource) => {
    const audienceOk =
      audienceFilter === "All" ||
      String(resource.audience || "").toLowerCase().includes(audienceFilter.toLowerCase());
    const kindOk = kindFilter === "All" || resource.resourceKind === kindFilter || resource.type === kindFilter;
    const text = [
      resource.title,
      resource.type,
      resource.audience,
      resource.action,
      resource.description,
      resource.topic,
      ...(resource.tags || []),
    ]
      .join(" ")
      .toLowerCase();
    const searchOk = !search || text.includes(search);
    return audienceOk && kindOk && searchOk;
  });

  const quickResources = resources.length ? resources.slice(0, 3) : categoryResources.slice(0, 3);
  const wellnessTips = HEALTH_WELLNESS_TIPS;
  const favoriteResources = AURA_RESOURCE_LIBRARY.filter((resource) => favoriteResourceIds.includes(resource.id));
  const audienceFilters = ["All", "Students", "Teachers", "Parents", "Administrators"];
  const kindFilters = ["All", "Poster", "Classroom Lesson", "Teacher Guide", "Parent Guide", "Administrator Playbook", "Student Activity"];

  const runAction = (kind: ResourceActionKind, resource: RecommendedResource) => {
    if (kind === "preview") onPreviewResource(resource);
    if (kind === "download") onDownloadResource(resource);
    if (kind === "print") onPrintResource(resource);
    if (kind === "email") onEmailResource(resource);
    if (kind === "favorite") onFavoriteResource(resource);
  };

  return (
    <div className="resource-command-panel aura-resource-center">
      <div className="resource-center-hero">
        <div className="resource-center-orb">AURA</div>
        <div className="resource-command-copy">
          <div className="resource-kicker">AURA Resource Center™</div>
          <div className="resource-title">School Wellness Resources & Guides</div>
          <div className="resource-subtitle">
            Casey curates posters, classroom lessons, parent guides, teacher scripts, administrator playbooks, and student activities from the current AURA signal pattern.
          </div>
        </div>
        <div className="resource-command-actions">
          <button className="aura-primary-btn" type="button" onClick={onGenerateReport}>
            Generate School Report
          </button>
          <button className="aura-ghost-btn" type="button" onClick={onExportAura} disabled={isExportingAura}>
            {isExportingAura ? "Exporting..." : "Export School CSV"}
          </button>
        </div>
      </div>

      <div className="casey-resource-recommendation">
        <div className="casey-resource-badge">CASEY RECOMMENDS</div>
        <div>
          <strong>{hasAuraActivity ? `Focus first on ${leadingCategory.title}.` : "Keep prevention resources visible."}</strong>
          <p>
            {hasAuraActivity
              ? `${leadingCategory.caseyRecommendation} Begin with the poster, follow with the 20-minute classroom lesson, and send the parent guide if the pattern continues beyond one reporting period.`
              : "AURA is not showing an elevated pattern right now. This is the ideal time to use prevention posters, advisory lessons, and parent conversation guides before a concern grows."}
          </p>
        </div>
      </div>

      <div className="resource-quick-row" aria-label="Top recommended resources">
        {quickResources.map((resource, idx) => (
          <button
            key={`${resource.id}-${idx}`}
            type="button"
            className="resource-quick-chip"
            onClick={() => onPreviewResource(resource)}
            title={`Preview ${resource.title}`}
          >
            <span className="resource-quick-type">{resource.icon || "📄"} {resource.type}</span>
            <span className="resource-quick-title">{resource.title}</span>
          </button>
        ))}
      </div>

      <div className="resource-category-tabs" aria-label="AURA Resource Categories">
        {AURA_RESOURCE_CATEGORIES.map((category) => (
          <button
            key={category.id}
            type="button"
            className={`resource-category-tab ${activeCategory.id === category.id ? "active" : ""}`}
            onClick={() => setActiveCategoryId(category.id)}
          >
            <span>{category.icon}</span>
            <strong>{category.title}</strong>
            <small>{category.tagline}</small>
          </button>
        ))}
      </div>

      <div className="resource-library-toolbar">
        <input
          className="resource-search-input"
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          placeholder="Search anxiety, bullying, TikTok, grief, vaping, suicide, parent tips..."
        />
        <div className="resource-filter-row">
          {audienceFilters.map((filter) => (
            <button
              key={filter}
              type="button"
              className={`resource-filter-chip ${audienceFilter === filter ? "active" : ""}`}
              onClick={() => setAudienceFilter(filter)}
            >
              {filter}
            </button>
          ))}
        </div>
        <div className="resource-filter-row resource-filter-kind-row">
          {kindFilters.map((filter) => (
            <button
              key={filter}
              type="button"
              className={`resource-filter-chip ${kindFilter === filter ? "active" : ""}`}
              onClick={() => setKindFilter(filter)}
            >
              {filter}
            </button>
          ))}
        </div>
      </div>

      <div className="resource-category-header">
        <div>
          <div className="resource-kicker">{activeCategory.icon} {activeCategory.title}</div>
          <div className="resource-title resource-title-small">{activeCategory.tagline}</div>
          <div className="resource-subtitle">{activeCategory.caseyRecommendation}</div>
        </div>
        <div className="resource-count-pill">{filteredResources.length} resources</div>
      </div>

      <div className="resource-grid resource-center-grid">
        {filteredResources.map((resource, idx) => {
          const isFavorite = favoriteResourceIds.includes(resource.id);
          const isRecommended = recommendedIds.has(resource.id) || resource.featured;
          return (
            <div key={`${resource.id}-${idx}`} className={`resource-card resource-center-card ${isRecommended ? "recommended" : ""}`}>
              <div className="resource-card-top">
                <span className="resource-type-pill">{resource.icon || "📄"} {resource.type}</span>
                <span className="resource-action-pill">{resource.action}</span>
              </div>
              <div className="resource-card-title">{resource.title}</div>
              <div className="resource-card-audience">{resource.audience}</div>
              <div className="resource-card-desc">{resource.description}</div>
              <div className="resource-card-meta">
                <span>{resource.estimatedTime || "School-ready"}</span>
                {isRecommended ? <span>★ Casey Pick</span> : null}
              </div>
              <div className="resource-card-actions">
                <button type="button" onClick={() => runAction("preview", resource)}>👁 Preview</button>
                <button type="button" onClick={() => runAction("download", resource)}>📄 Download</button>
                <button type="button" onClick={() => runAction("print", resource)}>🖨 Print</button>
                <button type="button" onClick={() => runAction("email", resource)}>✉ Email</button>
                <button type="button" className={isFavorite ? "favorite active" : "favorite"} onClick={() => runAction("favorite", resource)}>
                  {isFavorite ? "★ Saved" : "☆ Save"}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {favoriteResources.length ? (
        <div className="favorite-resource-strip">
          <div className="wellness-tip-strip-head">
            <span className="wellness-tip-kicker">Saved Favorites</span>
            <span className="wellness-tip-subtitle">Quick access to resources your school uses most often</span>
          </div>
          <div className="wellness-tip-grid">
            {favoriteResources.map((resource) => (
              <button
                key={resource.id}
                type="button"
                className="wellness-tip-chip"
                onClick={() => onPreviewResource(resource)}
                title={`Preview favorite: ${resource.title}`}
              >
                <span className="wellness-tip-title">{resource.icon || "★"} {resource.title}</span>
                <span className="wellness-tip-action">{resource.type}</span>
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <div className="wellness-tip-strip" aria-label="Health and wellness quick tips">
        <div className="wellness-tip-strip-head">
          <span className="wellness-tip-kicker">Health & Wellness Tips</span>
          <span className="wellness-tip-subtitle">Quick supports that stay available across every category</span>
        </div>
        <div className="wellness-tip-grid">
          {wellnessTips.map((tip, idx) => (
            <button
              key={`${tip.title}-${idx}`}
              type="button"
              className="wellness-tip-chip"
              onClick={() => onPreviewResource(tip)}
              title={`Preview wellness tip: ${tip.title}`}
            >
              <span className="wellness-tip-title">{tip.title}</span>
              <span className="wellness-tip-action">{tip.action}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ================= UI PIECES =================
function MetricTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="metric-tile">
      <div className="metric-label">{label}</div>
      <div className="metric-value">{value}</div>
    </div>
  );
}

function KpiCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: SeverityTone;
}) {
  const icon = label.toLowerCase().includes("support")
    ? "⌁"
    : label.toLowerCase().includes("wellness")
      ? "♡"
      : label.toLowerCase().includes("status")
        ? "!"
        : "▦";

  const caption = label.toLowerCase().includes("support")
    ? "Total Signals"
    : label.toLowerCase().includes("wellness")
      ? "Overall Score"
      : label.toLowerCase().includes("status")
        ? "Immediate Attention"
        : "Projected Signals";

  return (
    <div className={`kpi-card ${tone ? `tone-${tone}` : ""}`}>
      <div className="kpi-label">{label}</div>
      <div className="kpi-body">
        <div className={`kpi-icon ${tone ? `tone-${tone}` : ""}`}>{icon}</div>
        <div>
          <div className="kpi-value">{value}</div>
          <div className="kpi-caption">{caption}</div>
        </div>
      </div>
    </div>
  );
}

function LegendDot({
  tone,
  label,
}: {
  tone: SeverityTone;
  label: string;
}) {
  return (
    <div className="legend-item">
      <span className={`legend-dot ${tone}`} />
      <span>{label}</span>
    </div>
  );
}

function ChartSvg({
  rows,
  yAxisTicks,
  xAxisLabels,
  lines,
  legends,
  large = false,
}: {
  rows: ChartBucket[];
  yAxisTicks: number[];
  xAxisLabels: Array<ChartBucket & { idx: number }>;
  lines: Array<"schoolRisk" | "incidentVolume" | "highRisk">;
  legends: string[];
  large?: boolean;
}) {
  const width = 560;
  const height = large ? 230 : 190;
  const plotLeft = 46;
  const plotRight = 16;
  const plotTop = 34;
  const plotBottom = 34;
  const plotWidth = width - plotLeft - plotRight;
  const plotHeight = height - plotTop - plotBottom;

  const maxTick = Math.max(1, ...yAxisTicks);
  const strokeMap: Record<string, string> = {
    schoolRisk: "#57d6ff",
    incidentVolume: "#ff7a66",
    highRisk: "#f9c85b",
  };

  function pointFor(row: ChartBucket, idx: number, key: "schoolRisk" | "incidentVolume" | "highRisk") {
    const x = rows.length > 1 ? plotLeft + (idx / (rows.length - 1)) * plotWidth : plotLeft;
    const y = plotTop + plotHeight - (Number(row[key] || 0) / maxTick) * plotHeight;
    return { x, y };
  }

  function pathFor(key: "schoolRisk" | "incidentVolume" | "highRisk") {
    return rows
      .map((row, idx) => {
        const point = pointFor(row, idx, key);
        return `${idx === 0 ? "M" : "L"} ${point.x.toFixed(1)} ${point.y.toFixed(1)}`;
      })
      .join(" ");
  }

  return (
    <div className="trend-svg-shell">
      <div className="trend-legends">
        {legends.map((label, idx) => {
          const key = lines[idx];
          return (
            <div className="trend-legend-item" key={`${label}-${idx}`}>
              <span
                className="trend-legend-line"
                style={{ background: strokeMap[key] || "#57d6ff" }}
              />
              <span>{label}</span>
            </div>
          );
        })}
      </div>

      <svg className="trend-svg" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
        <text x="4" y="16" fill="#9fb6d8" fontSize="12">
          Y: incidents
        </text>

        <text x={width - 132} y={height - 6} fill="#9fb6d8" fontSize="12">
          X: reporting period
        </text>

        {yAxisTicks.map((tick) => {
          const y = plotTop + plotHeight - (tick / maxTick) * plotHeight;
          return (
            <React.Fragment key={`tick-${tick}`}>
              <text x="6" y={y + 4} fill="#9fb6d8" fontSize="11">
                {tick}
              </text>
              <line
                x1={plotLeft}
                x2={width - plotRight}
                y1={y}
                y2={y}
                stroke="rgba(130,180,255,0.14)"
                strokeWidth="1"
              />
            </React.Fragment>
          );
        })}

        {lines.map((key) => (
          <path
            key={key}
            d={pathFor(key)}
            fill="none"
            stroke={strokeMap[key]}
            strokeWidth={key === "schoolRisk" ? 4 : 3}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ))}

        {xAxisLabels.map((row) => {
          const x =
            rows.length > 1
              ? plotLeft + (row.idx / (rows.length - 1)) * plotWidth
              : plotLeft;

          return (
            <text
              key={`${row.fullLabel}-${row.idx}`}
              x={x}
              y={height - 18}
              fill="#9fb6d8"
              fontSize="10"
              textAnchor="middle"
            >
              {row.label}
            </text>
          );
        })}
      </svg>
    </div>
  );
}

// ================= STYLES =================
const dashboardStyles = `
  * { box-sizing: border-box; }

  .aura-admin-shell {
    min-height: 100vh;
    height: 100vh;
    overflow-y: auto;
    overflow-x: hidden;
    background:
      radial-gradient(circle at top left, rgba(65, 223, 255, 0.32), transparent 28%),
      radial-gradient(circle at bottom right, rgba(109, 76, 255, 0.28), transparent 26%),
      linear-gradient(135deg, #54c9ff 0%, #518ad0 45%, #496cb8 100%);
    padding: 20px 24px 80px;
    color: #eef5ff;
    font-family: Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    position: relative;
  }

  .aura-admin-backdrop {
    position: absolute;
    inset: 0;
    background:
      radial-gradient(circle at center, rgba(0, 119, 255, 0.14), transparent 32%),
      radial-gradient(circle at 30% 20%, rgba(133, 245, 255, 0.14), transparent 20%);
    pointer-events: none;
  }

  .aura-admin-page {
    width: min(1480px, calc(100vw - 48px));
    max-width: 1480px;
    margin: 0 auto;
    position: relative;
    z-index: 1;
  }

  .aura-topbar,
  .aura-header-row,
  .enroll-panel,
  .weather-board,
  .aura-login-card {
    border: 1px solid rgba(112, 161, 255, 0.18);
    background: linear-gradient(180deg, rgba(5, 17, 44, 0.94), rgba(2, 9, 29, 0.95));
    box-shadow:
      0 20px 80px rgba(0, 0, 0, 0.32),
      inset 0 1px 0 rgba(255,255,255,0.05);
  }

  .aura-topbar {
    border-radius: 22px;
    padding: 14px 18px;
    display: flex;
    justify-content: space-between;
    gap: 14px;
    align-items: center;
  }

  .aura-brand {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .aura-brand-logo {
    width: 106px;
    height: 50px;
    border-radius: 16px;
    background:
      radial-gradient(circle at 30% 35%, rgba(99, 221, 255, 0.45), transparent 25%),
      linear-gradient(135deg, #071935, #112f68);
    border: 1px solid rgba(126, 201, 255, 0.22);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 22px;
    font-weight: 900;
    letter-spacing: 1.4px;
  }

  .aura-brand-title {
    font-size: 22px;
    font-weight: 800;
    letter-spacing: 0.5px;
  }

  .aura-brand-subtitle {
    color: #a8bbdb;
    font-size: 13px;
    margin-top: 4px;
  }

  .aura-topbar-actions {
    display: flex;
    gap: 10px;
  }

  .aura-header-row {
    margin-top: 10px;
    border-radius: 18px;
    padding: 10px 14px;
    display: flex;
    justify-content: space-between;
    gap: 14px;
    align-items: center;
    flex-wrap: wrap;
  }

  .aura-mode-tabs,
  .aura-range-group {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
  }

  .aura-tab,
  .aura-range-btn,
  .aura-primary-btn,
  .aura-ghost-btn {
    border-radius: 14px;
    border: 1px solid rgba(132, 177, 255, 0.2);
    padding: 9px 13px;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.18s ease;
  }

  .aura-tab,
  .aura-range-btn,
  .aura-ghost-btn {
    color: #dbe8ff;
    background: rgba(12, 31, 74, 0.78);
  }

  .aura-tab-active,
  .aura-range-btn.active,
  .aura-primary-btn {
    color: white;
    background: linear-gradient(135deg, #2356a8, #3d87d7 55%, #6f5cff 100%);
    box-shadow: 0 10px 30px rgba(65, 122, 255, 0.28);
  }

  .aura-alert {
    margin-top: 10px;
    border-radius: 14px;
    padding: 10px 12px;
    font-weight: 600;
    font-size: 13px;
  }

  .aura-alert-error {
    background: rgba(133, 21, 48, 0.28);
    border: 1px solid rgba(255, 110, 146, 0.25);
    color: #ffd9e3;
  }

  .aura-alert-ok {
    background: rgba(18, 82, 71, 0.28);
    border: 1px solid rgba(100, 255, 208, 0.22);
    color: #d9fff4;
  }

  .aura-alert-info {
    background: rgba(21, 61, 119, 0.24);
    border: 1px solid rgba(132, 181, 255, 0.18);
    color: #d9e9ff;
  }

  .weather-board {
    margin-top: 10px;
    border-radius: 24px;
    padding: 12px;
    display: grid;
    grid-template-columns: 300px minmax(0, 1fr);
    gap: 12px;
  }

  .weather-left-rail {
    display: flex;
    flex-direction: column;
    gap: 10px;
    min-width: 0;
  }

  .rail-card,
  .kpi-card,
  .map-panel,
  .insight-panel,
  .metric-tile {
    border-radius: 18px;
    background: linear-gradient(180deg, rgba(3, 18, 52, 0.98), rgba(1, 10, 31, 0.98));
    border: 1px solid rgba(104, 157, 255, 0.16);
    box-shadow: inset 0 1px 0 rgba(255,255,255,0.04);
  }

  .rail-card,
  .kpi-card,
  .map-panel,
  .insight-panel,
  .metric-tile {
    padding: 12px;
  }

  .rail-title,
  .panel-title {
    color: #dbe6fb;
    text-transform: uppercase;
    letter-spacing: 1.4px;
    font-size: 12px;
    font-weight: 800;
    margin-bottom: 10px;
  }

  .school-alert-card {
    border-radius: 16px;
    padding: 12px;
    border: 1px solid rgba(255,255,255,0.08);
    margin-bottom: 10px;
  }

  .school-alert-card.low {
    background: linear-gradient(180deg, rgba(14, 59, 83, 0.92), rgba(8, 31, 52, 0.94));
  }

  .school-alert-card.moderate {
    background: linear-gradient(180deg, rgba(85, 54, 10, 0.92), rgba(44, 28, 10, 0.94));
  }

  .school-alert-card.elevated,
  .school-alert-card.critical {
    background: linear-gradient(180deg, rgba(108, 12, 32, 0.92), rgba(58, 8, 22, 0.94));
  }

  .school-alert-top {
    display: flex;
    justify-content: space-between;
    gap: 8px;
    align-items: flex-start;
  }

  .school-alert-name {
    font-size: 16px;
    font-weight: 800;
    line-height: 1.1;
  }

  .severity-pill {
    border-radius: 999px;
    padding: 5px 9px;
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 1px;
    font-weight: 800;
    white-space: nowrap;
  }

  .severity-pill.low {
    background: rgba(65, 227, 187, 0.16);
    color: #8ff0d2;
  }

  .severity-pill.moderate {
    background: rgba(255, 210, 98, 0.16);
    color: #ffd56a;
  }

  .severity-pill.elevated {
    background: rgba(255, 156, 94, 0.16);
    color: #ffae72;
  }

  .severity-pill.critical {
    background: rgba(255, 104, 140, 0.16);
    color: #ff97b3;
  }

  .school-alert-signals {
    margin-top: 10px;
    color: #f0dfe6;
    line-height: 1.4;
    font-size: 13px;
  }

  .school-alert-score {
    margin-top: 8px;
    font-size: 30px;
    font-weight: 900;
    text-align: right;
    color: #ffe79f;
  }

  .secondary-school-card {
    border-radius: 14px;
    padding: 12px;
    background: rgba(14, 30, 68, 0.9);
    border: 1px solid rgba(106, 162, 255, 0.16);
  }

  .secondary-school-name {
    font-size: 15px;
    font-weight: 700;
  }

  .secondary-school-meta {
    font-size: 12px;
    color: #b4c3df;
    margin-top: 4px;
  }

  .signal-check-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px 8px;
  }

  .signal-check-row {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #dce7fb;
    font-size: 13px;
  }

  .signal-check-badge {
    width: 16px;
    height: 16px;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: rgba(68, 190, 255, 0.2);
    color: #91dbff;
    font-size: 10px;
    font-weight: 900;
  }

  .signal-check-badge.safe {
    background: rgba(92, 240, 177, 0.18);
    color: #94f7ca;
  }

  .rail-divider {
    height: 1px;
    background: rgba(130, 168, 230, 0.12);
    margin: 10px 0;
  }

  .weather-main {
    display: flex;
    flex-direction: column;
    gap: 10px;
    min-width: 0;
  }

  .kpi-grid {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 10px;
  }

  .kpi-label {
    text-transform: uppercase;
    letter-spacing: 1.1px;
    color: #90a7cb;
    font-size: 11px;
    font-weight: 800;
  }

  .kpi-value {
    margin-top: 8px;
    font-size: 24px;
    font-weight: 900;
  }

  .map-panel {
    padding: 8px;
  }

  .map-surface {
    position: relative;
    min-height: 335px;
    border-radius: 20px;
    overflow: hidden;
    background:
      radial-gradient(circle at center, rgba(71, 153, 255, 0.14), transparent 28%),
      radial-gradient(circle at 30% 62%, rgba(255, 236, 142, 0.1), transparent 14%),
      linear-gradient(180deg, rgba(2, 20, 55, 0.98), rgba(0, 8, 25, 1));
    border: 1px solid rgba(113, 173, 255, 0.16);
  }

  .map-grid {
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(rgba(115, 170, 255, 0.05) 1px, transparent 1px),
      linear-gradient(90deg, rgba(115, 170, 255, 0.05) 1px, transparent 1px);
    background-size: 32px 32px;
    opacity: 0.28;
  }

  .orbit {
    position: absolute;
    border-radius: 999px;
    border: 1px solid rgba(112, 170, 255, 0.12);
  }

  .orbit-1 { width: 70%; height: 70%; left: 15%; top: 8%; }
  .orbit-2 { width: 50%; height: 50%; left: 25%; top: 18%; }
  .orbit-3 { width: 86%; height: 62%; left: 7%; top: 19%; }

  .map-world-overlay {
    position: absolute;
    inset: 18px 22px 36px 22px;
    background-image: url(${auraWorldMapBg});
    background-repeat: no-repeat;
    background-position: center;
    background-size: contain;
    opacity: 0.16;
    filter: drop-shadow(0 0 12px rgba(120, 190, 255, 0.06));
    pointer-events: none;
    z-index: 0;
  }

  .map-grid,
  .orbit {
    z-index: 1;
  }

  .pulse-marker,
  .map-tooltip {
    z-index: 2;
  }

  .pulse-marker {
    position: absolute;
    width: 24px;
    height: 24px;
    transform: translate(-50%, -50%);
  }

  .pulse-dot {
    width: 24px;
    height: 24px;
    border-radius: 999px;
    position: relative;
    z-index: 2;
    box-shadow: 0 0 24px currentColor;
  }

  .pulse-dot.small {
    width: 15px;
    height: 15px;
  }

  .pulse-marker.low { color: #4ff0c6; }
  .pulse-marker.moderate { color: #ffd766; }
  .pulse-marker.elevated { color: #ff9c5b; }
  .pulse-marker.critical { color: #ff5f7d; }

  .pulse-marker.low .pulse-dot { background: #4ff0c6; }
  .pulse-marker.moderate .pulse-dot { background: #ffd766; }
  .pulse-marker.elevated .pulse-dot { background: #ff9c5b; }
  .pulse-marker.critical .pulse-dot { background: #ff5f7d; }

  .pulse-ring {
    position: absolute;
    inset: -18px;
    border-radius: 999px;
    border: 2px solid currentColor;
    opacity: 0.35;
    animation: pulseAura 2.8s infinite ease-out;
  }

  @keyframes pulseAura {
    0% { transform: scale(0.8); opacity: 0.38; }
    70% { transform: scale(1.55); opacity: 0; }
    100% { transform: scale(1.55); opacity: 0; }
  }

  .map-tooltip {
    position: absolute;
    right: 16px;
    bottom: 14px;
    width: 220px;
    border-radius: 16px;
    padding: 14px;
    background: linear-gradient(180deg, rgba(89, 7, 24, 0.86), rgba(39, 6, 18, 0.9));
    border: 1px solid rgba(255, 108, 144, 0.18);
  }

  .map-tooltip-school {
    font-size: 16px;
    font-weight: 800;
    line-height: 1.2;
    margin-bottom: 8px;
  }

  .map-tooltip-prob {
    margin-top: 10px;
    color: #ffd76e;
    font-size: 17px;
    font-weight: 800;
  }

  .map-tooltip-copy {
    margin-top: 10px;
    color: #f4e2e7;
    line-height: 1.42;
    font-size: 13px;
  }

  .legend-row {
    padding: 8px 4px 0;
    display: flex;
    gap: 16px;
    align-items: center;
    flex-wrap: wrap;
  }

  .legend-item {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: #c8d8f1;
    font-size: 12px;
  }

  .legend-dot {
    width: 11px;
    height: 11px;
    border-radius: 999px;
    display: inline-block;
  }

  .legend-dot.low { background: #4ff0c6; }
  .legend-dot.moderate { background: #ffd766; }
  .legend-dot.elevated { background: #ff9c5b; }
  .legend-dot.critical { background: #ff5f7d; }

  .legend-meta {
    color: #dce8ff;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
  }

  .insight-grid {
    display: grid;
    grid-template-columns: 1.15fr 1fr;
    gap: 10px;
  }

  .insight-body {
    display: grid;
    grid-template-columns: 250px minmax(0, 1fr);
    gap: 16px;
    align-items: stretch;
  }

  .insight-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .insight-row {
    display: flex;
    align-items: center;
    gap: 10px;
    color: #eff5ff;
    font-size: 14px;
  }

  .check-pill {
    width: 17px;
    height: 17px;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: rgba(94, 218, 255, 0.14);
    color: #8be1ff;
    font-size: 10px;
    font-weight: 900;
  }

  .trend-up {
    margin-left: auto;
    color: #ff9a6d;
    font-size: 14px;
    font-weight: 900;
  }

  .trend-svg-shell {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
    height: 100%;
    min-height: 230px;
  }

  .trend-legends {
    display: flex;
    align-items: center;
    gap: 14px;
    flex-wrap: wrap;
    padding: 0 2px;
  }

  .trend-legend-item {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: #c8d8f1;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
  }

  .trend-legend-line {
    width: 18px;
    height: 3px;
    border-radius: 999px;
    display: inline-block;
  }

  .trend-svg {
    width: 100%;
    min-height: 190px;
    flex: 1 1 auto;
    border-radius: 14px;
    background: linear-gradient(180deg, rgba(3, 18, 52, 0.8), rgba(2, 10, 28, 0.86));
    border: 1px solid rgba(111, 164, 255, 0.12);
  }

  .trend-dates {
    margin-top: 8px;
    display: flex;
    justify-content: space-between;
    color: #8fa8cf;
    font-size: 11px;
  }

  .footer-strip {
    border-radius: 16px;
    padding: 10px 12px;
    background: linear-gradient(180deg, rgba(3,18,52,0.98), rgba(1,10,31,0.98));
    border: 1px solid rgba(104,157,255,0.16);
    display: flex;
    justify-content: space-between;
    gap: 10px;
    align-items: center;
    flex-wrap: wrap;
  }

  .live-signals {
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;
  }

  .footer-label {
    color: #dce9ff;
    text-transform: uppercase;
    letter-spacing: 1.2px;
    font-size: 12px;
    font-weight: 800;
  }

  .signal-chip {
    padding: 7px 11px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 700;
    border: 1px solid rgba(255,255,255,0.06);
  }

  .signal-chip.critical {
    background: rgba(145,22,45,0.6);
    color: #ff93ad;
  }

  .signal-chip.moderate {
    background: rgba(122,88,18,0.48);
    color: #ffd56a;
  }

  .footer-actions {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
  }

  .enroll-panel {
    margin-top: 14px;
    margin-bottom: 60px;
    border-radius: 18px;
    padding: 16px;
    display: grid;
    grid-template-columns: 1.12fr 1fr;
    gap: 14px;
    align-items: center;
  }

  .enroll-kicker {
    color: #8eb8ff;
    text-transform: uppercase;
    letter-spacing: 1.4px;
    font-size: 11px;
    font-weight: 800;
  }

  .enroll-title {
    font-size: 20px;
    font-weight: 800;
    margin-top: 4px;
  }

  .enroll-subtitle {
    margin-top: 8px;
    color: #b8c8e3;
    line-height: 1.45;
    max-width: 650px;
    font-size: 13px;
  }

  .enroll-actions {
    display: flex;
    gap: 10px;
    margin-top: 14px;
    flex-wrap: wrap;
  }

  .enroll-meta-line {
    margin-top: 10px;
    color: #a8bbdb;
    font-size: 12px;
  }

  .enroll-metrics {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
  }

  .metric-label {
    color: #8ca4cb;
    text-transform: uppercase;
    font-size: 10px;
    letter-spacing: 1.2px;
    font-weight: 800;
  }

  .metric-value {
    font-size: 22px;
    font-weight: 800;
    margin-top: 8px;
  }

  .enrollment-overlay {
    position: fixed;
    inset: 0;
    z-index: 9999;
    background: rgba(2, 8, 22, 0.62);
    backdrop-filter: blur(6px);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
  }

  .enrollment-modal-shell {
    width: min(1120px, calc(100vw - 48px));
    max-height: calc(100vh - 48px);
    overflow: auto;
    border-radius: 24px;
  }

  .aura-login-shell {
    min-height: calc(100vh - 48px);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .aura-login-card {
    width: 100%;
    max-width: 520px;
    border-radius: 24px;
    padding: 28px;
  }

  .aura-login-logo {
    width: 130px;
    margin-bottom: 18px;
  }

  .aura-login-title {
    font-size: 28px;
    font-weight: 800;
  }

  .aura-login-subtitle {
    color: #b8c8e3;
    margin-top: 8px;
    line-height: 1.5;
  }

  .aura-login-form {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 18px;
  }



  .casey-advisor-panel {
    display: grid;
    grid-template-columns: 140px minmax(0, 1fr);
    gap: 16px;
    border: 1px solid rgba(125, 211, 252, 0.18);
    border-radius: 22px;
    background:
      radial-gradient(circle at top left, rgba(56, 189, 248, 0.18), transparent 30%),
      linear-gradient(135deg, rgba(8, 25, 58, 0.94), rgba(11, 35, 78, 0.86));
    box-shadow: 0 18px 40px rgba(0, 0, 0, 0.24);
    padding: 16px;
    margin-bottom: 14px;
  }

  .casey-advisor-portrait {
    min-height: 180px;
    border-radius: 20px;
    background: linear-gradient(180deg, rgba(234, 248, 255, 0.95), rgba(175, 231, 255, 0.72));
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    position: relative;
    overflow: hidden;
  }

  .casey-face {
    width: 88px;
    height: 66px;
    border-radius: 26px;
    background: #071427;
    position: relative;
    box-shadow: inset 0 0 16px rgba(125, 211, 252, 0.22), 0 10px 22px rgba(0, 0, 0, 0.28);
  }

  .casey-eye {
    position: absolute;
    top: 22px;
    width: 13px;
    height: 19px;
    border-radius: 999px;
    background: #7dd3fc;
    box-shadow: 0 0 14px rgba(125, 211, 252, 0.9);
  }

  .casey-eye.left { left: 24px; }
  .casey-eye.right { right: 24px; }

  .casey-smile {
    position: absolute;
    left: 34px;
    top: 44px;
    width: 20px;
    height: 9px;
    border-bottom: 3px solid #7dd3fc;
    border-radius: 0 0 999px 999px;
  }

  .casey-coat {
    margin-top: 12px;
    background: #ffffff;
    color: #0b2b55;
    border-radius: 999px;
    padding: 7px 14px;
    font-size: 11px;
    font-weight: 1000;
    letter-spacing: 0.08em;
    box-shadow: 0 8px 18px rgba(0, 0, 0, 0.16);
  }

  .casey-advisor-main {
    min-width: 0;
  }

  .casey-advisor-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 14px;
  }

  .casey-advisor-title {
    color: #ffffff;
    font-size: 20px;
    font-weight: 1000;
    line-height: 1.1;
  }

  .casey-advisor-subtitle {
    color: #d9ecff;
    font-size: 13px;
    line-height: 1.35;
    margin-top: 6px;
    max-width: 920px;
  }

  .casey-language-select {
    border: 1px solid rgba(125, 211, 252, 0.26);
    background: rgba(4, 14, 36, 0.86);
    color: #eaf4ff;
    border-radius: 12px;
    padding: 10px 12px;
    font-weight: 900;
    outline: none;
  }

  .casey-quick-prompts {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin: 12px 0;
  }

  .casey-prompt-chip {
    border: 1px solid rgba(125, 211, 252, 0.24);
    background: rgba(8, 28, 62, 0.88);
    color: #bfeaff;
    border-radius: 999px;
    padding: 8px 11px;
    font-size: 12px;
    font-weight: 1000;
    cursor: pointer;
  }

  .casey-prompt-chip:hover {
    border-color: rgba(125, 211, 252, 0.52);
    transform: translateY(-1px);
  }

  .casey-chat-window {
    min-height: 92px;
    max-height: 172px;
    overflow: auto;
    border: 1px solid rgba(127, 176, 255, 0.12);
    border-radius: 16px;
    background: rgba(3, 10, 26, 0.42);
    padding: 10px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .casey-chat-bubble {
    border-radius: 14px;
    padding: 9px 11px;
    font-size: 13px;
    line-height: 1.35;
    max-width: 92%;
  }

  .casey-chat-bubble.casey {
    align-self: flex-start;
    color: #eaf4ff;
    background: rgba(14, 165, 198, 0.18);
    border: 1px solid rgba(125, 211, 252, 0.2);
  }

  .casey-chat-bubble.admin {
    align-self: flex-end;
    color: #ffffff;
    background: rgba(84, 105, 255, 0.34);
    border: 1px solid rgba(147, 160, 255, 0.28);
  }

  .casey-chat-form {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 10px;
    margin-top: 10px;
  }

  .casey-chat-input {
    border: 1px solid rgba(125, 211, 252, 0.2);
    background: rgba(4, 14, 36, 0.86);
    color: #ffffff;
    border-radius: 13px;
    padding: 12px 14px;
    outline: none;
  }

  .casey-chat-input::placeholder {
    color: rgba(219, 236, 255, 0.55);
  }

  .resource-command-panel {
    margin: 14px 0 16px;
    border: 1px solid rgba(127, 176, 255, 0.18);
    border-radius: 20px;
    background:
      linear-gradient(135deg, rgba(15, 35, 76, 0.94), rgba(7, 18, 46, 0.92)),
      radial-gradient(circle at top right, rgba(87, 144, 255, 0.16), transparent 36%);
    box-shadow: inset 0 1px 0 rgba(255,255,255,0.06), 0 16px 34px rgba(0,0,0,0.18);
    padding: 14px;
  }

  .resource-command-main {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 16px;
    align-items: center;
  }

  .resource-kicker {
    color: #7dd3fc;
    font-size: 11px;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    font-weight: 1000;
    margin-bottom: 6px;
  }

  .resource-title {
    color: #f8fbff;
    font-size: 17px;
    line-height: 1.2;
    font-weight: 1000;
  }

  .resource-subtitle {
    margin-top: 5px;
    color: #c8d7f0;
    font-size: 13px;
    line-height: 1.35;
    max-width: 820px;
  }

  .resource-command-actions {
    display: flex;
    gap: 10px;
    align-items: center;
    justify-content: flex-end;
    flex-wrap: wrap;
  }

  .resource-quick-row {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 12px;
  }

  .resource-quick-chip {
    appearance: none;
    border: 1px solid rgba(125, 211, 252, 0.18);
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    max-width: 290px;
    border-radius: 999px;
    background: rgba(8, 22, 58, 0.72);
    color: #edf6ff;
    padding: 7px 10px;
    font-size: 12px;
    font-weight: 900;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .resource-quick-type {
    color: #9ee8ff;
    font-size: 10px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    flex: 0 0 auto;
  }

  .resource-quick-title {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .resource-quick-chip:hover {
    transform: translateY(-1px);
    border-color: rgba(125, 211, 252, 0.42);
    box-shadow: 0 10px 24px rgba(0, 0, 0, 0.22);
  }

  .resource-dropdown {
    margin-top: 10px;
    border-top: 1px solid rgba(127, 176, 255, 0.12);
    padding-top: 8px;
  }

  .resource-dropdown summary {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    color: #eaf4ff;
    cursor: pointer;
    font-weight: 1000;
    list-style: none;
  }

  .resource-dropdown summary::-webkit-details-marker {
    display: none;
  }

  .resource-dropdown summary::before {
    content: "▸";
    color: #7dd3fc;
    margin-right: 6px;
    transition: transform 0.16s ease;
  }

  .resource-dropdown[open] summary::before {
    transform: rotate(90deg);
  }

  .resource-summary-count {
    margin-left: auto;
    color: #9db7df;
    font-size: 12px;
    font-weight: 900;
  }

  .resource-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 10px;
    margin-top: 12px;
  }

  .wellness-tip-strip {
    margin-top: 12px;
    border: 1px solid rgba(125, 211, 252, 0.12);
    border-radius: 16px;
    padding: 10px 12px;
    background: linear-gradient(135deg, rgba(6, 20, 48, 0.78), rgba(10, 36, 69, 0.5));
  }

  .wellness-tip-strip-head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 8px;
  }

  .wellness-tip-kicker {
    color: #9ee8ff;
    font-size: 11px;
    font-weight: 1000;
    letter-spacing: 0.12em;
    text-transform: uppercase;
  }

  .wellness-tip-subtitle {
    color: #9db7df;
    font-size: 11px;
    font-weight: 800;
  }

  .wellness-tip-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 8px;
  }

  .wellness-tip-chip {
    border: 1px solid rgba(125, 211, 252, 0.16);
    border-radius: 12px;
    padding: 9px 10px;
    background: rgba(2, 12, 32, 0.72);
    color: #eaf4ff;
    font: inherit;
    text-align: left;
    cursor: pointer;
    min-height: 58px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 4px;
  }

  .wellness-tip-chip:hover {
    transform: translateY(-1px);
    border-color: rgba(125, 211, 252, 0.44);
    box-shadow: 0 12px 26px rgba(35, 119, 255, 0.16);
  }

  .wellness-tip-chip:focus-visible {
    outline: 2px solid #8fdcff;
    outline-offset: 2px;
  }

  .wellness-tip-title {
    color: #ffffff;
    font-size: 12px;
    font-weight: 1000;
    line-height: 1.15;
  }

  .wellness-tip-action {
    color: #7dd3fc;
    font-size: 10px;
    font-weight: 900;
  }

  .resource-card {
    border: 1px solid rgba(127, 176, 255, 0.14);
    border-radius: 16px;
    background: rgba(4, 14, 36, 0.82);
    padding: 12px;
    min-height: 168px;
    width: 100%;
    text-align: left;
    font: inherit;
    display: flex;
    flex-direction: column;
    cursor: pointer;
  }

  .resource-card-clickable:hover {
    transform: translateY(-1px);
    border-color: rgba(132, 211, 255, 0.56);
    box-shadow: 0 14px 30px rgba(35, 119, 255, 0.16);
  }

  .resource-card-clickable:focus-visible {
    outline: 2px solid #8fdcff;
    outline-offset: 2px;
  }

  .resource-card-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 10px;
  }

  .resource-type-pill,
  .resource-action-pill {
    border-radius: 999px;
    padding: 5px 8px;
    font-size: 10px;
    font-weight: 1000;
    line-height: 1;
  }

  .resource-type-pill {
    color: #08213f;
    background: #9ee8ff;
  }

  .resource-action-pill {
    color: #fff4cf;
    background: rgba(180, 126, 30, 0.44);
    border: 1px solid rgba(255, 216, 122, 0.22);
  }

  .resource-card-title {
    color: #ffffff;
    font-size: 15px;
    line-height: 1.15;
    font-weight: 1000;
    margin-bottom: 6px;
  }

  .resource-card-audience {
    color: #9ee8ff;
    font-size: 12px;
    font-weight: 900;
    margin-bottom: 8px;
  }

  .resource-card-desc {
    color: #c8d7f0;
    font-size: 12px;
    line-height: 1.35;
    flex: 1;
  }

  .resource-download-btn {
    margin-top: 12px;
    width: 100%;
    border: 1px solid rgba(125, 211, 252, 0.28);
    border-radius: 12px;
    padding: 9px 10px;
    background: linear-gradient(135deg, rgba(35, 111, 214, 0.92), rgba(93, 103, 255, 0.86));
    color: #ffffff;
    cursor: pointer;
    font-size: 12px;
    font-weight: 1000;
    box-shadow: 0 10px 22px rgba(37, 99, 235, 0.18);
  }

  .resource-download-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 14px 26px rgba(37, 99, 235, 0.24);
  }

  .aura-input {
    width: 100%;
    border-radius: 14px;
    border: 1px solid rgba(127,176,255,0.18);
    background: rgba(8,22,58,0.82);
    color: #f1f7ff;
    padding: 14px 14px;
    font-size: 15px;
    outline: none;
  }

  .aura-input::placeholder {
    color: #8ea8cf;
  }

  /* ================= WOW AURA OPERATIONS CENTER ================= */
  .ops-center-board { display: grid; gap: 18px; }
  .ops-hero-header { display:flex; align-items:center; justify-content:space-between; gap:18px; padding:18px 22px; border:1px solid rgba(123,191,255,.22); border-radius:24px; background:radial-gradient(circle at 10% 10%, rgba(47,210,255,.18), transparent 28%), linear-gradient(135deg, rgba(2,17,45,.96), rgba(6,28,68,.92)); box-shadow:0 24px 60px rgba(0,0,0,.24); }
  .ops-brand-lockup { display:flex; align-items:center; gap:18px; min-width:0; }
  .ops-aura-logo { width:104px; height:54px; display:grid; place-items:center; border-radius:16px; color:#9fe9ff; font-size:28px; font-weight:1000; letter-spacing:2px; background:linear-gradient(135deg, rgba(20,107,191,.72), rgba(6,23,55,.92)); border:1px solid rgba(126,216,255,.35); box-shadow:inset 0 0 22px rgba(68,199,255,.16); }
  .ops-title { color:#fff; font-size:clamp(22px,2.2vw,32px); font-weight:1000; letter-spacing:.4px; line-height:1.05; text-transform:uppercase; }
  .ops-subtitle { margin-top:5px; color:#b9d8ff; font-weight:700; }
  .ops-admin-card { display:flex; align-items:center; gap:12px; padding:11px 14px; border-radius:18px; border:1px solid rgba(139,203,255,.22); background:rgba(4,18,43,.66); }
  .ops-admin-avatar { width:42px; height:42px; border-radius:50%; display:grid; place-items:center; color:#08203e; font-weight:1000; background:linear-gradient(135deg,#bff4ff,#37cdf1); }
  .ops-admin-name { color:#fff; font-weight:1000; white-space:nowrap; }
  .ops-admin-role { color:#98bde6; font-size:12px; font-weight:800; }
  .ops-pulse-strip { display:flex; align-items:center; gap:10px; overflow-x:auto; padding:10px; border-radius:18px; border:1px solid rgba(123,191,255,.20); background:rgba(3,15,38,.84); }
  .ops-live-pill, .ops-pulse-item { display:flex; align-items:center; gap:8px; min-height:42px; padding:9px 14px; border-radius:14px; background:rgba(9,31,70,.8); border:1px solid rgba(116,195,255,.16); color:#cfe6ff; font-weight:900; white-space:nowrap; }
  .ops-live-pill { color:#fff; text-transform:uppercase; letter-spacing:.7px; }
  .ops-live-pill span { width:9px; height:9px; border-radius:50%; background:#46f4a8; box-shadow:0 0 14px rgba(70,244,168,.9); }
  .ops-pulse-item strong.up, .ops-trend-row strong.up { color:#ff7c89; }
  .ops-pulse-item strong.steady, .ops-trend-row strong.steady { color:#67e7aa; }
  .ops-pulse-item.right { margin-left:auto; }
  .ops-top-grid { display:grid; grid-template-columns:minmax(0,1.8fr) minmax(320px,.9fr); gap:16px; }
  .ops-kpi-matrix { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:12px; }
  .ops-forecast-row { display:grid; grid-template-columns:repeat(4,minmax(0,1fr)); gap:12px; }
  .ops-forecast-card, .ops-panel-card, .ops-map-card { border:1px solid rgba(123,191,255,.20); border-radius:22px; background:radial-gradient(circle at top left, rgba(49,153,255,.13), transparent 35%), rgba(4,18,43,.84); box-shadow:0 18px 40px rgba(0,0,0,.16); }
  .ops-forecast-card { padding:16px; text-align:center; }
  .ops-forecast-label { color:#9fc9f6; font-size:12px; font-weight:1000; text-transform:uppercase; letter-spacing:.8px; }
  .ops-forecast-icon { margin-top:6px; font-size:32px; }
  .ops-forecast-title { margin-top:6px; color:#fff; font-weight:1000; min-height: 34px; display: flex; align-items: center; justify-content: center; line-height: 1.15; }
  .ops-forecast-pct { margin-top:4px; color:#ffe177; font-size:28px; font-weight:1000; }
  .ops-forecast-caption { color:#9fc9f6; font-size:12px; font-weight:850; line-height:1.25; }
  .ops-main-grid { display:grid; grid-template-columns:minmax(0,1.65fr) minmax(320px,.8fr); gap:16px; }
  .ops-map-card, .ops-panel-card { padding:16px; }
  .ops-section-head { display:flex; align-items:center; justify-content:space-between; gap:12px; margin-bottom:12px; }
  .ops-section-title { color:#fff; font-size:16px; font-weight:1000; letter-spacing:.3px; }
  .ops-section-subtitle { color:#93b6df; font-size:12px; font-weight:800; margin-top:3px; }
  .ops-map-toggle { padding:9px 12px; border-radius:12px; color:#cfe9ff; background:rgba(11,38,83,.78); border:1px solid rgba(126,203,255,.2); font-weight:900; }
  .ops-map-panel { box-shadow:none; border-radius:18px; }
  .ops-right-column { display:grid; gap:16px; }
  .ops-trend-list, .ops-action-list { display:grid; gap:8px; margin-top:12px; }
  .ops-trend-row, .ops-action-list div { display:flex; justify-content:space-between; gap:10px; padding:11px 12px; border-radius:12px; color:#d7ecff; font-weight:850; background:rgba(7,28,64,.75); border:1px solid rgba(114,184,255,.12); }
  .ops-action-list div { display:block; line-height:1.35; }
  .ops-analytics-grid { display:grid; grid-template-columns:minmax(0,1fr) minmax(0,1fr); gap:16px; }
  .ops-footer-strip { margin-top:0; }


  .aura-header-row-clean {
    justify-content: flex-end;
  }

  .aura-filter-title {
    margin-right: auto;
    color: #dff3ff;
    font-size: 12px;
    font-weight: 1000;
    letter-spacing: 0.12em;
    text-transform: uppercase;
  }

  .ops-pulse-strip {
    overflow: hidden;
    position: relative;
  }

  .ops-pulse-track {
    display: flex;
    align-items: center;
    gap: 10px;
    width: max-content;
    min-width: 200%;
    animation: auraWellnessTicker 24s linear infinite;
    will-change: transform;
  }

  .ops-pulse-strip:hover .ops-pulse-track {
    animation-play-state: paused;
  }

  @keyframes auraWellnessTicker {
    0% { transform: translateX(0); }
    100% { transform: translateX(-50%); }
  }

  .casey-official-img {
    position: absolute;
    inset: 12px 8px 8px 8px;
    width: calc(100% - 16px);
    height: calc(100% - 24px);
    object-fit: contain;
    z-index: 5;
    filter: drop-shadow(0 14px 20px rgba(0,0,0,.28));
  }

  .casey-figure-fallback {
    position: absolute;
    inset: 16px 10px 12px;
    display: grid;
    place-items: center;
    align-content: center;
    gap: 8px;
  }

  .casey-head {
    position: relative;
    width: 92px;
    height: 74px;
    display: grid;
    place-items: center;
    border-radius: 42px;
    background: linear-gradient(180deg, #ffffff, #dcecff);
    box-shadow: 0 16px 34px rgba(0,0,0,.22), inset 0 -8px 18px rgba(64,139,206,.18);
  }

  .casey-ear {
    position: absolute;
    top: 25px;
    width: 16px;
    height: 28px;
    border-radius: 12px;
    background: linear-gradient(180deg,#bceeff,#57b9ee);
    box-shadow: 0 0 12px rgba(91,217,255,.45);
  }

  .casey-ear.left { left: -10px; }
  .casey-ear.right { right: -10px; }

  .casey-head .casey-face {
    position: relative;
    width: 72px;
    height: 48px;
    border-radius: 24px;
    background: radial-gradient(circle at 50% 30%, rgba(69,217,255,.17), transparent 42%), #06182b;
    box-shadow: inset 0 0 18px rgba(86,205,255,.23), 0 10px 18px rgba(0,0,0,.18);
  }

  .casey-body {
    position: relative;
    width: 86px;
    height: 82px;
    border-radius: 22px 22px 18px 18px;
    background: linear-gradient(90deg, #f7fbff 0 38%, #123054 38% 62%, #f7fbff 62% 100%);
    border: 1px solid rgba(255,255,255,.72);
    box-shadow: 0 14px 22px rgba(0,0,0,.18);
  }

  .casey-heart {
    position: absolute;
    left: 50%;
    top: 18px;
    transform: translateX(-50%);
    color: #65e8ff;
    text-shadow: 0 0 18px rgba(101,232,255,.9);
    font-size: 22px;
    line-height: 1;
  }

  .casey-lapel {
    position: absolute;
    top: 8px;
    width: 22px;
    height: 56px;
    background: rgba(215,235,255,.9);
    clip-path: polygon(0 0, 100% 18%, 54% 100%, 0 100%);
  }

  .casey-lapel.left { left: 8px; }
  .casey-lapel.right { right: 8px; transform: scaleX(-1); }

  .casey-advisor-portrait .casey-eye {
    width: 10px;
    height: 18px;
    border-radius: 999px;
    background: #6ee7ff;
    box-shadow: 0 0 12px rgba(110,231,255,.95);
    top: 15px;
  }

  .casey-advisor-portrait .casey-smile {
    width: 24px;
    height: 12px;
    border-bottom: 3px solid #6ee7ff;
    border-radius: 0 0 18px 18px;
    left: 50%;
    bottom: 9px;
    transform: translateX(-50%);
  }

  .pulse-ring {
    animation: auraPulseRing 2.4s ease-out infinite;
  }

  @keyframes auraPulseRing {
    0% { transform: scale(.65); opacity: .9; }
    100% { transform: scale(2.6); opacity: 0; }
  }

  .map-tooltip-prob {
    color: #ffe177;
    font-weight: 1000;
  }


  /* Section 1 enterprise tweaks: official Casey + color-coded AURA forecast cards */
  .casey-advisor-portrait {
    min-height: 236px;
    background: linear-gradient(180deg, rgba(235, 249, 255, 0.98), rgba(180, 231, 255, 0.82));
  }

  .casey-official-img {
    inset: 10px 6px 16px 6px;
    width: calc(100% - 12px);
    height: calc(100% - 26px);
    object-fit: contain;
    object-position: center bottom;
    border-radius: 18px;
  }

  .casey-figure-fallback {
    opacity: 0;
    pointer-events: none;
  }

  .ops-forecast-card {
    position: relative;
    overflow: hidden;
    min-height: 164px;
  }

  .ops-forecast-card::before {
    content: "";
    position: absolute;
    inset: 0;
    opacity: .28;
    pointer-events: none;
  }

  .forecast-card-0::before { background: radial-gradient(circle at 18% 18%, rgba(255, 210, 79, .58), transparent 35%); }
  .forecast-card-1::before { background: radial-gradient(circle at 18% 18%, rgba(255, 160, 70, .52), transparent 35%); }
  .forecast-card-2::before { background: radial-gradient(circle at 18% 18%, rgba(255, 96, 122, .48), transparent 35%); }
  .forecast-card-3::before { background: radial-gradient(circle at 18% 18%, rgba(167, 107, 255, .48), transparent 35%); }

  .forecast-card-0 .ops-forecast-title,
  .forecast-card-0 .ops-forecast-pct { color: #ffe36e; }
  .forecast-card-1 .ops-forecast-title,
  .forecast-card-1 .ops-forecast-pct { color: #ffcf5f; }
  .forecast-card-2 .ops-forecast-title,
  .forecast-card-2 .ops-forecast-pct { color: #ff6f8f; }
  .forecast-card-3 .ops-forecast-title,
  .forecast-card-3 .ops-forecast-pct { color: #ffad4d; }

  .ops-forecast-caption {
    max-width: 92%;
    margin-left: auto;
    margin-right: auto;
  }

@media (max-width:1180px) { .ops-top-grid, .ops-main-grid, .ops-analytics-grid { grid-template-columns:1fr; } .ops-forecast-row { grid-template-columns:repeat(2,minmax(0,1fr)); } }
  @media (max-width:700px) { .ops-hero-header, .ops-section-head { flex-direction:column; align-items:flex-start; } .ops-forecast-row, .ops-kpi-matrix { grid-template-columns:1fr; } }

  @media (max-width: 1180px) {
    .weather-board {
      grid-template-columns: 1fr;
    }

    .weather-left-rail {
      order: 2;
    }

    .weather-main {
      order: 1;
    }

    .kpi-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .insight-grid,
    .enroll-panel,
    .resource-grid {
      grid-template-columns: 1fr;
    }

    .resource-command-main {
      grid-template-columns: 1fr;
    }

    .resource-command-actions {
      justify-content: flex-start;
    }
  }

  @media (max-width: 760px) {
    .aura-admin-shell {
      padding: 12px;
    }

    .aura-topbar {
      flex-direction: column;
      align-items: flex-start;
    }

    .insight-body {
      grid-template-columns: 1fr;
    }

    .kpi-grid {
      grid-template-columns: 1fr;
    }

    .signal-check-grid {
      grid-template-columns: 1fr;
    }

    .map-surface {
      min-height: 280px;
    }

    .aura-brand-title {
      font-size: 18px;
    }

    .enroll-title {
      font-size: 18px;
    }

    .enrollment-overlay {
      padding: 12px;
    }

    .enrollment-modal-shell {
      width: calc(100vw - 24px);
      max-height: calc(100vh - 24px);
    }
  }

  /* AURA Gold Section One polish */
  .casey-advisor-panel {
    grid-template-columns: 250px minmax(0, 1fr);
    gap: 22px;
    border-color: rgba(56, 189, 248, 0.34);
    background:
      radial-gradient(circle at 14% 18%, rgba(23, 128, 204, 0.34), transparent 30%),
      linear-gradient(135deg, rgba(5, 17, 42, 0.98), rgba(8, 30, 70, 0.94));
  }

  .casey-advisor-portrait {
    min-height: 420px;
    border-radius: 20px;
    background:
      radial-gradient(circle at 55% 18%, rgba(29, 105, 171, 0.58), transparent 34%),
      linear-gradient(180deg, #06162d 0%, #092142 54%, #041020 100%);
    border: 1px solid rgba(125, 211, 252, 0.26);
    box-shadow: inset 0 0 36px rgba(56, 189, 248, 0.11), 0 22px 44px rgba(0,0,0,.28);
  }

  .casey-official-img {
    inset: 10px 8px 64px 8px;
    width: calc(100% - 16px);
    height: calc(100% - 74px);
    object-fit: contain;
    filter: drop-shadow(0 18px 24px rgba(0,0,0,.42));
  }

  .casey-coat {
    position: absolute;
    left: 50%;
    bottom: 18px;
    transform: translateX(-50%);
    min-width: 120px;
    text-align: center;
    background: linear-gradient(180deg, #f8fbff, #dff2ff);
    color: #08203d;
    border-radius: 999px;
    padding: 10px 18px;
    font-size: 18px;
    font-weight: 1000;
    letter-spacing: 0.06em;
    z-index: 8;
  }

  .casey-chat-window {
    min-height: 190px;
    max-height: 230px;
    border-color: rgba(56, 189, 248, 0.2);
  }

  .casey-chat-bubble {
    padding: 13px 16px;
    font-size: 15px;
    line-height: 1.42;
  }

  .casey-chat-bubble.casey {
    background: linear-gradient(135deg, rgba(14, 116, 144, 0.36), rgba(8, 47, 73, 0.55));
    border-color: rgba(34, 211, 238, 0.32);
  }

  .kpi-card {
    padding: 22px;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    min-height: 210px;
  }

  .kpi-body {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 22px;
    flex: 1;
  }

  .kpi-icon {
    width: 88px;
    height: 88px;
    border-radius: 999px;
    display: grid;
    place-items: center;
    font-size: 52px;
    font-weight: 1000;
    color: #67e8f9;
    border: 3px solid rgba(34, 211, 238, 0.68);
    box-shadow: 0 0 34px rgba(34, 211, 238, 0.24), inset 0 0 24px rgba(34, 211, 238, 0.12);
  }

  .kpi-icon.tone-critical {
    color: #fb7185;
    border-color: rgba(251, 113, 133, 0.76);
    box-shadow: 0 0 34px rgba(251, 113, 133, 0.28), inset 0 0 24px rgba(251, 113, 133, 0.12);
  }

  .kpi-caption {
    margin-top: 8px;
    color: #a7c7ff;
    font-size: 13px;
    font-weight: 800;
    text-align: center;
  }

  .ops-forecast-card {
    min-height: 178px;
    border-width: 1px;
    box-shadow: inset 0 0 28px rgba(255,255,255,.035), 0 18px 40px rgba(0,0,0,.22);
  }

  .forecast-card-0 {
    background: radial-gradient(circle at 15% 22%, rgba(248, 113, 113, .28), transparent 38%), linear-gradient(135deg, rgba(90, 10, 20, .94), rgba(24, 20, 48, .94));
    border-color: rgba(248, 113, 113, .72);
  }
  .forecast-card-1 {
    background: radial-gradient(circle at 18% 25%, rgba(234, 179, 8, .28), transparent 36%), linear-gradient(135deg, rgba(72, 48, 8, .92), rgba(24, 30, 52, .94));
    border-color: rgba(234, 179, 8, .68);
  }
  .forecast-card-2 {
    background: radial-gradient(circle at 20% 25%, rgba(236, 72, 153, .3), transparent 36%), linear-gradient(135deg, rgba(76, 12, 74, .92), rgba(20, 28, 62, .94));
    border-color: rgba(236, 72, 153, .7);
  }
  .forecast-card-3 {
    background: radial-gradient(circle at 22% 20%, rgba(124, 58, 237, .3), transparent 38%), linear-gradient(135deg, rgba(45, 24, 96, .92), rgba(16, 28, 64, .94));
    border-color: rgba(124, 58, 237, .7);
  }

  .ops-forecast-metric-row {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 18px;
    margin-top: 8px;
  }

  .ops-forecast-delta {
    color: #fb7185;
    font-size: 15px;
    line-height: 1.15;
    font-weight: 1000;
    max-width: 92px;
    text-align: left;
  }

  @media (max-width: 1100px) {
    .casey-advisor-panel { grid-template-columns: 1fr; }
    .casey-advisor-portrait { min-height: 280px; }
  }

  .casey-figure-fallback {
    display: none !important;
  }


  /* AURA Gold lower-half reflow: map stays dominant, trends/actions move into map blank space, charts move up */
  .ops-main-grid-reflow {
    grid-template-columns: minmax(0, 1.55fr) minmax(390px, .95fr);
    align-items: stretch;
  }

  .ops-weather-command-card {
    display: flex;
    flex-direction: column;
    gap: 14px;
    min-height: 0;
  }

  .ops-weather-command-card .ops-map-panel {
    flex: 1 1 auto;
  }

  .ops-map-intel-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 14px;
    margin-top: 2px;
  }

  .ops-panel-card-embedded {
    background: rgba(3, 16, 39, .72);
    border-color: rgba(111, 191, 255, .18);
    box-shadow: inset 0 0 24px rgba(44, 163, 255, .045);
  }

  .ops-chart-column {
    grid-template-rows: minmax(320px, 1fr) minmax(320px, 1fr);
  }

  .ops-chart-panel {
    min-height: 320px;
    display: flex;
    flex-direction: column;
  }

  .ops-chart-panel .insight-body {
    flex: 1 1 auto;
  }

  .ops-chart-panel:first-child .insight-body {
    display: flex;
    flex-direction: column;
    gap: 14px;
    align-items: stretch;
  }

  .ops-chart-panel:first-child .insight-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px 12px;
  }

  .ops-chart-panel:first-child .insight-row {
    min-height: 28px;
    padding: 6px 8px;
    border-radius: 12px;
    background: rgba(5, 23, 58, .42);
    border: 1px solid rgba(111, 191, 255, .12);
  }

  .ops-chart-panel .trend-svg-shell {
    flex: 1 1 auto;
  }

  .ops-chart-panel:first-child .trend-svg-shell {
    min-height: 250px;
  }

  .ops-chart-panel .trend-svg {
    min-height: 260px;
  }

  .ops-chart-panel:first-child .trend-svg {
    min-height: 250px;
  }

  .pulse-ring.soft {
    border-color: rgba(77, 229, 183, .5);
    box-shadow: 0 0 18px rgba(77, 229, 183, .18);
  }

  .pulse-ring.moderate-ring {
    border-color: rgba(255, 215, 102, .65);
    box-shadow: 0 0 20px rgba(255, 215, 102, .24);
  }

  .pulse-dot.green {
    background: #4de5b7;
    box-shadow: 0 0 18px rgba(77, 229, 183, .8);
  }

  @media (max-width:1180px) {
    .ops-main-grid-reflow,
    .ops-map-intel-row {
      grid-template-columns: 1fr;
    }
  }


  .roster-panel {
    margin-top: 22px;
    border-radius: 28px;
    padding: 22px;
    border: 1px solid rgba(125, 200, 255, 0.18);
    background: linear-gradient(180deg, rgba(7, 19, 37, 0.95), rgba(9, 25, 48, 0.92));
    box-shadow: 0 22px 70px rgba(0, 0, 0, 0.28);
  }

  .roster-panel-header {
    display: flex;
    justify-content: space-between;
    gap: 18px;
    align-items: flex-start;
    margin-bottom: 18px;
  }

  .roster-forms {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;
    margin-bottom: 18px;
  }

  .roster-form-card,
  .roster-table-card {
    border-radius: 22px;
    padding: 16px;
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(125, 200, 255, 0.14);
  }

  .roster-form-title,
  .roster-table-title {
    font-weight: 950;
    color: #ffffff;
    margin-bottom: 12px;
  }

  .roster-form-card {
    display: grid;
    gap: 10px;
  }

  .roster-tables {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }

  .roster-table-scroll {
    overflow-x: auto;
  }

  .roster-table {
    width: 100%;
    border-collapse: collapse;
  }

  .roster-table th,
  .roster-table td {
    padding: 10px 10px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    text-align: left;
    color: #eef6ff;
    font-size: 0.9rem;
  }

  .roster-table th {
    color: #8dcfff;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    font-size: 0.74rem;
  }

  .aura-mini-btn {
    border: 1px solid rgba(125, 200, 255, 0.28);
    border-radius: 999px;
    background: rgba(10, 34, 62, 0.9);
    color: #eef6ff;
    padding: 7px 10px;
    font-weight: 850;
    cursor: pointer;
    white-space: nowrap;
  }

  .aura-mini-btn:disabled {
    opacity: 0.55;
    cursor: default;
  }

  @media (max-width: 980px) {
    .roster-panel-header,
    .roster-forms,
    .roster-tables {
      grid-template-columns: 1fr;
      flex-direction: column;
    }
  }

  .aura-resource-center {
    position: relative;
    overflow: hidden;
  }

  .resource-center-hero {
    position: relative;
    display: grid;
    grid-template-columns: auto 1fr auto;
    gap: 18px;
    align-items: center;
    padding: 22px;
    border-radius: 24px;
    background:
      radial-gradient(circle at 12% 18%, rgba(87, 214, 255, 0.28), transparent 22%),
      linear-gradient(135deg, rgba(10, 36, 78, 0.96), rgba(5, 17, 44, 0.96) 54%, rgba(21, 83, 121, 0.9));
    border: 1px solid rgba(129, 205, 255, 0.22);
  }

  .resource-center-orb {
    width: 86px;
    height: 86px;
    border-radius: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 950;
    letter-spacing: 0.13em;
    color: #eaffff;
    background:
      radial-gradient(circle at 36% 28%, rgba(147, 240, 255, 0.75), transparent 24%),
      linear-gradient(135deg, #06234d, #0d74b9 62%, #11d5d5);
    box-shadow: 0 18px 55px rgba(0, 178, 255, 0.2);
  }

  .casey-resource-recommendation {
    margin-top: 16px;
    display: grid;
    grid-template-columns: 170px 1fr;
    gap: 16px;
    align-items: start;
    padding: 18px;
    border-radius: 22px;
    background: linear-gradient(135deg, rgba(255, 248, 216, 0.13), rgba(14, 165, 233, 0.1));
    border: 1px solid rgba(255, 216, 112, 0.24);
  }

  .casey-resource-badge {
    border-radius: 999px;
    padding: 9px 12px;
    background: rgba(255, 202, 72, 0.18);
    color: #ffe082;
    font-size: 0.76rem;
    letter-spacing: 0.12em;
    font-weight: 950;
    text-align: center;
  }

  .casey-resource-recommendation p {
    margin: 7px 0 0;
    color: #c5d6f0;
    line-height: 1.5;
  }

  .resource-category-tabs {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 12px;
    margin-top: 18px;
  }

  .resource-category-tab {
    min-height: 138px;
    border-radius: 20px;
    padding: 16px;
    border: 1px solid rgba(129, 205, 255, 0.18);
    background: rgba(5, 18, 45, 0.82);
    color: #edf7ff;
    text-align: left;
    cursor: pointer;
    transition: transform 0.18s ease, border-color 0.18s ease, background 0.18s ease;
  }

  .resource-category-tab:hover,
  .resource-category-tab.active {
    transform: translateY(-2px);
    border-color: rgba(111, 224, 255, 0.62);
    background: linear-gradient(180deg, rgba(16, 75, 133, 0.92), rgba(7, 23, 58, 0.96));
  }

  .resource-category-tab span {
    display: block;
    font-size: 1.7rem;
    margin-bottom: 10px;
  }

  .resource-category-tab strong {
    display: block;
    font-size: 0.98rem;
    line-height: 1.22;
  }

  .resource-category-tab small {
    display: block;
    margin-top: 8px;
    color: #a9c0df;
    line-height: 1.25;
  }

  .resource-library-toolbar {
    margin-top: 18px;
    padding: 16px;
    border-radius: 20px;
    border: 1px solid rgba(129, 205, 255, 0.16);
    background: rgba(1, 12, 33, 0.52);
  }

  .resource-search-input {
    width: 100%;
    border-radius: 16px;
    border: 1px solid rgba(139, 197, 255, 0.22);
    background: rgba(3, 14, 34, 0.9);
    color: #f2fbff;
    padding: 13px 15px;
    outline: none;
    font-size: 0.96rem;
  }

  .resource-search-input::placeholder {
    color: #7897bd;
  }

  .resource-filter-row {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 12px;
  }

  .resource-filter-chip {
    border: 1px solid rgba(135, 196, 255, 0.22);
    border-radius: 999px;
    padding: 8px 11px;
    background: rgba(7, 27, 60, 0.86);
    color: #dceeff;
    font-weight: 850;
    cursor: pointer;
  }

  .resource-filter-chip.active {
    border-color: rgba(74, 222, 255, 0.72);
    background: rgba(35, 119, 182, 0.7);
  }

  .resource-category-header {
    margin-top: 18px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
  }

  .resource-title-small {
    font-size: 1.35rem;
  }

  .resource-count-pill {
    border-radius: 999px;
    padding: 9px 13px;
    background: rgba(87, 214, 255, 0.12);
    border: 1px solid rgba(87, 214, 255, 0.28);
    color: #c7f3ff;
    font-weight: 900;
    white-space: nowrap;
  }

  .resource-center-grid {
    margin-top: 14px;
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .resource-center-card {
    cursor: default;
    text-align: left;
    min-height: 318px;
    display: flex;
    flex-direction: column;
  }

  .resource-center-card.recommended {
    border-color: rgba(255, 216, 112, 0.42);
    box-shadow: 0 14px 45px rgba(255, 193, 7, 0.08);
  }

  .resource-card-meta {
    margin-top: auto;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    color: #9eb8d9;
    font-size: 0.78rem;
    font-weight: 850;
  }

  .resource-card-meta span {
    border-radius: 999px;
    padding: 5px 8px;
    background: rgba(255, 255, 255, 0.06);
  }

  .resource-card-actions {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 7px;
    margin-top: 14px;
  }

  .resource-card-actions button {
    border: 1px solid rgba(143, 199, 255, 0.22);
    border-radius: 11px;
    padding: 8px 7px;
    background: rgba(6, 28, 64, 0.88);
    color: #e9f7ff;
    font-size: 0.76rem;
    font-weight: 850;
    cursor: pointer;
  }

  .resource-card-actions button:hover,
  .resource-card-actions button.favorite.active {
    border-color: rgba(74, 222, 255, 0.62);
    background: rgba(18, 94, 145, 0.82);
  }

  .favorite-resource-strip {
    margin-top: 18px;
    padding: 16px;
    border-radius: 20px;
    border: 1px solid rgba(255, 216, 112, 0.22);
    background: rgba(255, 216, 112, 0.07);
  }

  @media (max-width: 1180px) {
    .resource-category-tabs,
    .resource-center-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .resource-center-hero {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 720px) {
    .casey-resource-recommendation,
    .resource-category-tabs,
    .resource-center-grid {
      grid-template-columns: 1fr;
    }

    .resource-card-actions {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

`;
